import React, { useEffect, useState, useCallback, memo, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { flushSync } from 'react-dom';
import './Dashboard.css';
import NLComponentModal from './NLComponentModal';
import DashboardOptionsEditor from './DashboardOptionsEditor';
import DashboardToolbar from './DashboardToolbar';
import DashboardGrid from './DashboardGrid';
import DashboardSkeleton from './DashboardSkeleton';
import DashboardConflictModal from './DashboardConflictModal';
import DashboardBackgroundModal from './DashboardBackgroundModal';
import SelectionBox from './SelectionBox';
import AlarmManagementModal from './AlarmManagementModal';
import AlarmPopup from './AlarmPopup';
import { calculateDashboardStyle, calculateHeaderStyle, calculateRowHeight} from '../utils/dashboardStyleUtils';
import { scrollToNewComponent } from '../utils/dashboardLayoutUtils';
import { useDashboardData } from '../hooks/useDashboardData';
import { useDashboardEditMode } from '../hooks/useDashboardEditMode';
import { useDashboardModals } from '../hooks/useDashboardModals';
import { useDashboardActions } from '../hooks/useDashboardActions';
import { useDashboardContext } from '../hooks/useDashboardContext';
import useMultiAlarmChecker from '../hooks/useMultiAlarmChecker';
import { getValidComponentsArray, isAuthError } from '../utils/dashboardHelpers';
import { showErrorMessage, showSuccessMessage } from '../utils/dashboardMessageUtils';
import { autoResizeComponents } from './DashboardAutoArrange';
import DashboardService from '../services/DashboardService';
import GlobalVariablesDebugPanel from './GlobalVariablesDebugPanel';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';
import { cloneDeep } from 'lodash';
import { 
  createGroup, 
  ungroupComponents, 
  getGroupComponentIds, 
  areComponentsInSameGroup,
  getComponentGroupId,
  cleanupEmptyGroups,
  setGroupLocked,
  isComponentInLockedGroup,
  resizeGroup,
  getGroupBoundingBox,
  updateGroup
} from '../utils/groupUtils';

/**
 * 대시보드 컴포넌트
 * 백엔드로부터 대시보드 템플릿을 가져와 화면에 렌더링합니다.
 * 
 * @param {Object} props
 * @param {string} props.dashboardId - 팝업으로 열 때 사용할 대시보드 ID
 * @param {boolean} props.isPopup - 팝업 모드 여부
 * @param {string} props.parentPopupId - 부모 팝업 ID (중첩 팝업의 경우)
 * @param {Object} props.initialVariables - 초기 변수값
 * @param {Function} props.onLoad - 로드 완료 콜백
 * 
 * ===== 팝업 자동 크기 모드용 Props =====
 * @param {Array} props.overrideLayout - 외부에서 주입된 레이아웃 (팝업 자동 크기용)
 *   - DashboardPopupContext에서 계산된 정규화된 레이아웃
 *   - 차트들이 (0,0)부터 시작하도록 좌표가 조정됨
 *   - 예: [{ i: 'chart1', x: 0, y: 0, w: 5, h: 7 }, { i: 'chart2', x: 2, y: 2, w: 4, h: 6 }]
 * 
 * @param {number} props.overrideGridColumns - 외부에서 주입된 그리드 컬럼 수
 *   - 바운딩 박스의 너비를 컬럼 수로 사용 (예: 6칸)
 *   - 기본값 12 대신 사용하여 차트들이 가로로 꽉 차게 만듦
 * 
 * @param {number} props.overrideRowHeight - 외부에서 주입된 그리드 행 높이 (px)
 *   - 정사각형 그리드 구현을 위한 행 높이
 *   - colWidth = rowHeight가 되도록 계산됨
 *   - 결과: 차트 비율 유지, 왜곡 없음
 */
const Dashboard = memo(({ 
  dashboardId = null, 
  isPopup = false, 
  currentPopupId = null,
  parentPopupId = null, 
  initialVariables = {},
  overrideLayout = null, // 팝업 자동 크기: 정규화된 레이아웃
  overrideGridColumns = null, // 팝업 자동 크기: 오버라이드 컬럼 수
  overrideRowHeight = null, // 팝업 자동 크기: 오버라이드 행 높이
  transparentMode = false, // 투명 대시보드 모드: 팝업 프레임 없이 차트만 표시
  onLoad = null,
  onError = null,
  printMode = false
}) => {
  // GlobalVariables context 가져오기
  const globalVariables = useGlobalVariables();
  const { globalUpdateMode, requestOverlayActive, removeVariablesBySource } = globalVariables || {};
  
  // 관리자 여부 상태
  const [isAdmin, setIsAdmin] = useState(false);
  
  // 알람 관리 모달 상태
  const [showAlarmManagement, setShowAlarmManagement] = useState(false);

  // 사용자 정보 조회로 관리자 여부 확인
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await axios.get('/api/users/me/', { withCredentials: true });
        if (!mounted) return;
        const data = resp?.data || {};
        setIsAdmin(!!(data.is_staff || data.is_superuser));
      } catch (_) {
        if (!mounted) return;
        setIsAdmin(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Use the custom hook for data management - globalVariables 전달
  const {
    dashboardTemplate,
    chartsData,
    loading,
    templateLoading,
    dataLoading,
    error,
    setDashboardTemplateAndUpdateData,
    pauseDataUpdates,
    resumeDataUpdates,
    refreshChartData,
    reloadDashboardTemplate, // 새로운 함수 추가
    updateSpecificComponents, // 특정 컴포넌트만 업데이트
    refreshAllChartsData // 전체 차트 새로고침
  } = useDashboardData(dashboardId, isPopup, globalVariables, initialVariables, { disableIntervals: !!printMode });
  
  // 활성 쿼리 상태 관리 (컴포넌트별 현재 사용 중인 쿼리)
  const [activeQueries, setActiveQueries] = useState({});
  
  // 현재 클릭된 차트 ID 추적 (독립선택 그룹 지원을 위해 배열로 변경)
  const [clickedChartIds, setClickedChartIds] = useState([]);
  
  // 차트별 클릭 상태 저장 (updateInterval 시 유지하기 위함)
  const chartClickStatesRef = useRef({});
  
  // activeQueries를 ref로도 관리 (handleChartClick에서 최신 값 참조용)
  const activeQueriesRef = useRef({});
  
  // activeQueries가 변경될 때마다 즉시 ref 업데이트
  activeQueriesRef.current = activeQueries;
  
  // activeQueries 변경 시 변경된 컴포넌트만 데이터 리로드
  const prevActiveQueriesRef = useRef({});
  
  // 간단한 deep equality 체크 함수
  const isClickParamsEqual = (params1, params2) => {
    if (!params1 && !params2) return true;
    if (!params1 || !params2) return false;
    return params1.clicked_category === params2.clicked_category &&
           params1.clicked_value === params2.clicked_value &&
           params1.clicked_series === params2.clicked_series &&
           params1.clicked_index === params2.clicked_index;
  };
  
  useEffect(() => {
    const prevActiveQueries = prevActiveQueriesRef.current;
    
    // 새로 추가되거나 변경된 컴포넌트만 찾기
    const changedComponentIds = Object.keys(activeQueries).filter(componentId => {
      const prevQuery = prevActiveQueries[componentId];
      const currentQuery = activeQueries[componentId];
      
      // 이전에 없었거나 쿼리가 변경된 경우
      return !prevQuery || 
             prevQuery.query !== currentQuery.query ||
             !isClickParamsEqual(prevQuery.clickParams, currentQuery.clickParams);
    });
    
    // 변경된 컴포넌트만 업데이트
    changedComponentIds.forEach(componentId => {
      const component = dashboardTemplate?.components?.find(c => c.id === componentId);
      if (component) {
        const activeQuery = activeQueries[componentId];
        
        // 클릭 상태 저장 (updateInterval 시 유지하기 위함)
        chartClickStatesRef.current[componentId] = {
          query: activeQuery.query,
          clickParams: activeQuery.clickParams,
          title: activeQuery.title,
          sourceComponent: activeQuery.sourceComponent
        };
        
        // 커스텀 쿼리와 파라미터로 데이터 로드
        // _click_params를 제외한 깨끗한 컴포넌트 사용
        const { _click_params, ...cleanComponent } = component;
        const customComponent = {
          ...cleanComponent,
          data_source: { ...component.data_source, query: activeQuery.query },
          title: activeQuery.title || component.title
        };
        refreshChartData(componentId, activeQuery.clickParams, customComponent);
      }
    });
    
    // 제거된 컴포넌트는 원본 쿼리로 복원
    const removedComponentIds = Object.keys(prevActiveQueries).filter(
      componentId => !activeQueries[componentId]
    );
    
    if (removedComponentIds.length > 0) {
    }
    
    removedComponentIds.forEach(componentId => {
      const component = dashboardTemplate?.components?.find(c => c.id === componentId);
      if (component) {
        // 클릭 상태 제거
        delete chartClickStatesRef.current[componentId];
        
        // null을 명시적으로 전달하여 클릭 파라미터 제거
        refreshChartData(componentId, null);
      }
    });
    
    // 현재 상태를 이전 상태로 저장
    prevActiveQueriesRef.current = activeQueries;
  }, [activeQueries, dashboardTemplate, refreshChartData]);

  // 차트 선택 상태 관리 - useDashboardEditMode 훅보다 먼저 선언
  // 다중 선택을 위해 배열로 변경
  const [selectedComponentIds, setSelectedComponentIds] = useState([]);
  
  // 디버깅용: 선택 상태가 변경될 때마다 로그 및 전역 변수 업데이트
  useEffect(() => {
    // 전역 변수에 현재 선택 상태 저장 (DashboardComponentRenderer에서 참조)
    window.__selectedComponentIds = selectedComponentIds;
    if (selectedComponentIds.length > 0) {
      console.log('[선택]', selectedComponentIds.length, '개:', selectedComponentIds);
    }
  }, [selectedComponentIds]);
  
  // 호환성을 위한 helper - 첫 번째 선택된 컴포넌트 ID 반환
  const selectedComponentId = selectedComponentIds.length > 0 ? selectedComponentIds[0] : null;
  
  // 다중 선택 helper 함수들
  const isComponentSelected = useCallback((componentId) => {
    return selectedComponentIds.includes(componentId);
  }, [selectedComponentIds]);
  
  const toggleComponentSelection = useCallback((componentId, isMultiSelect = false) => {
    if (!isMultiSelect) {
      // 단일 선택 모드: 해당 컴포넌트만 선택
      setSelectedComponentIds([componentId]);
    } else {
      // 다중 선택 모드: 토글
      setSelectedComponentIds(prev => 
        prev.includes(componentId) 
          ? prev.filter(id => id !== componentId)
          : [...prev, componentId]
      );
    }
  }, []);
  
  const selectComponents = useCallback((componentIds) => {
    setSelectedComponentIds(componentIds);
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedComponentIds([]);
  }, []);
  
  const addToSelection = useCallback((componentId) => {
    setSelectedComponentIds(prev => 
      prev.includes(componentId) ? prev : [...prev, componentId]
    );
  }, []);
  
  const removeFromSelection = useCallback((componentId) => {
    setSelectedComponentIds(prev => prev.filter(id => id !== componentId));
  }, []);

  // 개별 컴포넌트별 리사이즈 카운터 관리 (useDashboardEditMode 호출 전에 선언)
  const [componentResizeCounters, setComponentResizeCounters] = useState({});
  
  // 개별 컴포넌트 리사이즈 트리거 함수
  const triggerComponentResize = useCallback((componentId) => {
    setComponentResizeCounters(prev => ({
      ...prev,
      [componentId]: (prev[componentId] || 0) + 1
    }));
  }, []);

  // Use the custom hook for edit mode management
  const {
    editMode,
    layouts,
    hasLayoutChanged,
    setHasLayoutChanged,
    isSavingLayout,
    compactType,
    toggleEditMode,
    handleLayoutChange,
    handleBreakpointChange,
    saveLayout,
    cancelChanges,
    saveAndExit,
    exitWithoutSaving,
    toggleCompactType,
    setLayouts,
    syncLayoutAndTemplate,
    addComponentToLayout,
    removeComponentFromLayout,
    handleDragStart,
    handleDrag,
    handleDragStop,
    handleResizeStart,
    handleResize,
    handleResizeStop,
    backupCurrentTemplate,
    backupSpecificTemplate,
    enableEditModeWithoutBackup,
    enableEditModeWithHistoryPreservation,
    // 🔄 Undo/Redo 기능 추가
    canUndo,
    canRedo,
    undo,
    redo,
    clearHistory,
    undoStack,
    redoStack,
    savePreActionState,
    isUndoRedoOperation,
    // 🔧 새로운 compactType 제어 추가
    getEffectiveCompactType,
    isChatbotAddingComponent,
  } = useDashboardEditMode({ lg: [], md: [], sm: [], xs: [], xxs: [] }, dashboardTemplate, setDashboardTemplateAndUpdateData, selectedComponentIds, triggerComponentResize);

  // 편집 모드 종료 시 선택 상태 초기화
  useEffect(() => {
    if (!editMode && selectedComponentIds.length > 0) {
      console.log('[Dashboard] 편집 모드 종료 → 선택 상태 초기화');
      setSelectedComponentIds([]);
      window.__selectedComponentIds = [];
    }
  }, [editMode, selectedComponentIds.length]);

  // 알람 체크 기능 (팝업 모드, 프린트 모드, 편집 모드에서는 비활성화)
  const shouldCheckAlarm = !isPopup && !printMode && !editMode;
  const {
    activeAlarm,
    handleAlarmConfirm,
    handleAlarmSnooze,
    reloadAlarms
  } = useMultiAlarmChecker(shouldCheckAlarm, globalVariables);

  // ==================== 그룹 관련 함수들 (useDashboardEditMode 이후) ====================
  
  /**
   * 선택된 컴포넌트들을 그룹으로 묶기
   */
  const handleCreateGroup = useCallback(() => {
    if (!dashboardTemplate || selectedComponentIds.length < 2) {
      showErrorMessage('그룹을 만들려면 최소 2개 이상의 컴포넌트를 선택해야 합니다.');
      return;
    }

    // Undo를 위한 상태 저장
    savePreActionState?.();

    // 그룹 생성 (현재 레이아웃 전달하여 원본 크기 저장)
    const updatedTemplate = createGroup(
      dashboardTemplate, 
      selectedComponentIds,
      { name: `그룹 ${(dashboardTemplate.groups || []).length + 1}` },
      layouts.lg // 현재 레이아웃 전달
    );

    // 템플릿 업데이트
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setHasLayoutChanged(true);
    
    // 팝업 제거 (사용자 요청)
    // showSuccessMessage('컴포넌트들이 그룹으로 묶였습니다.');
  }, [dashboardTemplate, selectedComponentIds, savePreActionState, setDashboardTemplateAndUpdateData, setHasLayoutChanged, layouts.lg]);

  /**
   * 그룹 해제
   * @param {string} groupId - 해제할 그룹 ID (없으면 선택된 컴포넌트의 그룹 해제)
   */
  const handleUngroupComponents = useCallback((groupId = null) => {
    if (!dashboardTemplate) return;

    let targetGroupId = groupId;

    // groupId가 없으면 선택된 컴포넌트들의 그룹 확인
    if (!targetGroupId && selectedComponentIds.length > 0) {
      // 모두 같은 그룹에 속하는지 확인
      if (areComponentsInSameGroup(dashboardTemplate, selectedComponentIds)) {
        const firstComp = dashboardTemplate.components?.find(c => c.id === selectedComponentIds[0]);
        targetGroupId = firstComp?.group_id;
      }
    }

    if (!targetGroupId) {
      showErrorMessage('그룹 해제할 대상을 찾을 수 없습니다.');
      return;
    }

    // Undo를 위한 상태 저장
    savePreActionState?.();

    // 그룹 해제
    const updatedTemplate = ungroupComponents(dashboardTemplate, targetGroupId);

    // 템플릿 업데이트
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setHasLayoutChanged(true);
    
    // 팝업 제거 (사용자 요청)
    // showSuccessMessage('그룹이 해제되었습니다.');
  }, [dashboardTemplate, selectedComponentIds, savePreActionState, setDashboardTemplateAndUpdateData, setHasLayoutChanged]);

  /**
   * 그룹에 속한 모든 컴포넌트 선택
   * @param {string} groupId - 그룹 ID
   */
  const selectGroupComponents = useCallback((groupId) => {
    if (!dashboardTemplate || !groupId) return;
    
    const componentIds = getGroupComponentIds(dashboardTemplate, groupId);
    selectComponents(componentIds);
  }, [dashboardTemplate, selectComponents]);

  /**
   * 그룹 잠금/해제 토글
   * @param {string} groupId - 그룹 ID
   */
  const toggleGroupLock = useCallback((groupId) => {
    if (!dashboardTemplate || !groupId) return;

    const group = dashboardTemplate.groups?.find(g => g.id === groupId);
    if (!group) return;

    // Undo를 위한 상태 저장
    savePreActionState?.();

    // 잠금 상태 토글
    const updatedTemplate = setGroupLocked(dashboardTemplate, groupId, !group.locked);

    // 템플릿 업데이트
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setHasLayoutChanged(true);
    
    // 팝업 제거 (사용자 요청)
    // showSuccessMessage(group.locked ? '그룹 잠금이 해제되었습니다.' : '그룹이 잠겼습니다.');
  }, [dashboardTemplate, savePreActionState, setDashboardTemplateAndUpdateData, setHasLayoutChanged]);

  /**
   * 그룹 내 독립선택 토글 (런타임 모드 차트 클릭 시 독립 동작)
   * @param {string} groupId - 그룹 ID
   */
  const toggleGroupIsolation = useCallback((groupId) => {
    if (!dashboardTemplate || !groupId) return;

    const group = dashboardTemplate.groups?.find(g => g.id === groupId);
    if (!group) return;

    // Undo를 위한 상태 저장
    savePreActionState?.();

    // isolatedSelection 상태 토글
    const updatedTemplate = updateGroup(dashboardTemplate, groupId, {
      isolatedSelection: !group.isolatedSelection
    });

    // 템플릿 업데이트
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setHasLayoutChanged(true);
  }, [dashboardTemplate, savePreActionState, setDashboardTemplateAndUpdateData, setHasLayoutChanged]);

  /**
   * 개별 차트 독립선택 토글 (런타임 모드 차트 클릭 시 독립 동작)
   * @param {string} componentId - 컴포넌트 ID
   */
  const toggleChartIsolation = useCallback((componentId) => {
    if (!dashboardTemplate || !componentId) return;

    const component = dashboardTemplate.components?.find(c => c.id === componentId);
    if (!component) return;

    // Undo를 위한 상태 저장
    savePreActionState?.();

    // isolatedSelection 상태 토글
    const updatedTemplate = {
      ...dashboardTemplate,
      components: dashboardTemplate.components.map(c => {
        if (c.id === componentId) {
          return {
            ...c,
            isolatedSelection: !c.isolatedSelection
          };
        }
        return c;
      })
    };

    // 템플릿 업데이트
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setHasLayoutChanged(true);
  }, [dashboardTemplate, savePreActionState, setDashboardTemplateAndUpdateData, setHasLayoutChanged]);

  /**
   * 컴포넌트가 속한 그룹 ID 가져오기
   * @param {string} componentId - 컴포넌트 ID
   * @returns {string|null} 그룹 ID 또는 null
   */
  const getComponentGroup = useCallback((componentId) => {
    if (!dashboardTemplate || !componentId) return null;
    const component = dashboardTemplate.components?.find(c => c.id === componentId);
    return getComponentGroupId(component);
  }, [dashboardTemplate]);

  /**
   * 그룹 전체 리사이즈
   * @param {string} groupId - 그룹 ID
   * @param {Object} newBounds - 새로운 바운딩 박스 { x, y, w, h }
   */
  const handleGroupResize = useCallback((groupId, newBounds) => {
    if (!dashboardTemplate || !layouts.lg || !groupId) return;
    
    // 그룹 내 모든 컴포넌트 ID 미리 저장
    const componentIds = getGroupComponentIds(dashboardTemplate, groupId);
    
    // Undo를 위한 상태 저장
    savePreActionState?.();
    
    // resizeGroup 유틸리티 함수 사용
    const newLayout = resizeGroup(dashboardTemplate, layouts.lg, groupId, newBounds);
    
    // 그룹의 original_layout을 현재 레이아웃으로 업데이트 (개별 수정 반영)
    const updatedTemplate = { ...dashboardTemplate };
    if (updatedTemplate.groups) {
      updatedTemplate.groups = updatedTemplate.groups.map(g => {
        if (g.id === groupId) {
          // 리사이징 후의 레이아웃을 새 original로 저장
          const newOriginalLayout = newLayout
            .filter(item => componentIds.includes(item.i))
            .map(item => ({
              id: item.i,
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h
            }));
          
          return {
            ...g,
            original_layout: newOriginalLayout
          };
        }
        return g;
      });
    }
    
    // 레이아웃 업데이트
    setLayouts({
      lg: newLayout,
      md: newLayout,
      sm: newLayout,
      xs: newLayout,
      xxs: newLayout
    });
    
    // 템플릿 동기화 (업데이트된 groups 포함)
    setDashboardTemplateAndUpdateData(updatedTemplate);
    syncLayoutAndTemplate({ lg: newLayout }, 'group_resize');
    setHasLayoutChanged(true);
    
    // 그룹 내 모든 컴포넌트 리렌더링 트리거
    componentIds.forEach(componentId => {
      triggerComponentResize(componentId);
    });
    
    // 선택 상태 복원 (두 렌더 사이클 후에 확실히 복원)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSelectedComponentIds(componentIds);
      });
    });
  }, [dashboardTemplate, layouts.lg, setLayouts, syncLayoutAndTemplate, setHasLayoutChanged, savePreActionState, triggerComponentResize, setDashboardTemplateAndUpdateData]);

  /**
   * 그룹 전체 드래그 (바운딩 박스 드래그)
   * @param {string} groupId - 그룹 ID
   * @param {Object} delta - 이동 거리 { deltaX, deltaY } (그리드 단위)
   */
  const handleGroupDrag = useCallback((groupId, delta) => {
    if (!dashboardTemplate || !layouts.lg || !groupId) return;
    
    const componentIds = getGroupComponentIds(dashboardTemplate, groupId);
    
    // Undo를 위한 상태 저장
    savePreActionState?.();
    
    // 1단계: 그룹 내 모든 차트를 동일하게 이동 (경계 체크 없이)
    let tempLayout = layouts.lg.map(item => {
      if (componentIds.includes(item.i)) {
        return {
          ...item,
          x: item.x + delta.deltaX,
          y: item.y + delta.deltaY,
          moved: true,  // compaction 방지
          static: false // 고정 해제
        };
      }
      return item;
    });
    
    // 2단계: 그룹 바운딩 박스 계산
    const groupItems = tempLayout.filter(item => componentIds.includes(item.i));
    const minX = Math.min(...groupItems.map(item => item.x));
    const minY = Math.min(...groupItems.map(item => item.y));
    const maxX = Math.max(...groupItems.map(item => item.x + item.w));
    const maxY = Math.max(...groupItems.map(item => item.y + item.h));
    
    // 그리드 컬럼 수 (경계 체크용)
    const columns = dashboardTemplate?.layout?.columns || 12;
    
    // 3단계: 그룹이 화면 밖으로 나갔으면 전체를 화면 안으로 조정
    let adjustX = 0;
    let adjustY = 0;
    
    // 왼쪽 경계
    if (minX < 0) {
      adjustX = -minX; // 음수를 0으로 만들기 위한 조정량
    }
    // 오른쪽 경계
    else if (maxX > columns) {
      adjustX = columns - maxX; // maxX를 columns로 맞추기 위한 조정량
    }
    
    // 위쪽 경계
    if (minY < 0) {
      adjustY = -minY;
    }
    
    // 조정이 필요하면 그룹 전체를 다시 이동
    if (adjustX !== 0 || adjustY !== 0) {
      tempLayout = tempLayout.map(item => {
        if (componentIds.includes(item.i)) {
          return {
            ...item,
            x: item.x + adjustX,
            y: item.y + adjustY
          };
        }
        return item;
      });
    }
    
    const finalLayout = tempLayout;
    
    // 레이아웃 업데이트
    setLayouts({
      lg: finalLayout,
      md: finalLayout,
      sm: finalLayout,
      xs: finalLayout,
      xxs: finalLayout
    });
    
    // 템플릿 동기화
    syncLayoutAndTemplate({ lg: finalLayout }, 'group_drag');
    setHasLayoutChanged(true);
    
    // 그룹 내 모든 컴포넌트 선택 상태 유지
    setSelectedComponentIds(componentIds);
  }, [dashboardTemplate, layouts.lg, setLayouts, syncLayoutAndTemplate, setHasLayoutChanged, savePreActionState]);

  // ==================== 그룹 관련 함수들 끝 ====================

  // 편집 모드 전환 시 데이터 인터벌 일시정지/재개
  useEffect(() => {
    if (editMode) {
      pauseDataUpdates && pauseDataUpdates();
    } else {
      resumeDataUpdates && resumeDataUpdates();
    }
  }, [editMode, pauseDataUpdates, resumeDataUpdates]);

  // 🔄 isUndoRedoOperation 상태 추출 (삭제 - 위에서 직접 가져옴)

  // Use the custom hook for modal management
  const {
    showOptionsModal,
    openOptionsModal,
    closeOptionsModal,
    showNLComponentModal,
    openNLComponentModal,
    closeNLComponentModal,
    showConflictModal,
    openConflictModal,
    closeConflictModal,
    conflictInfo,
    conflictTemplate,
    componentToUpdate
  } = useDashboardModals();
  
  // 텍스트 라벨 전용 모달 제거 - 공용 모달로 통합
  
  // 배경 이미지 모달 상태 관리
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const openBackgroundModal = useCallback(() => setShowBackgroundModal(true), []);
  const closeBackgroundModal = useCallback(() => setShowBackgroundModal(false), []);
  
  // 배경 이미지 미반영 상태 보관 (저장 시 서버에 커밋)
  const [pendingBackground, setPendingBackground] = useState(null);
  
  // 복사/붙여넣기 상태 관리
  const [clipboardComponent, setClipboardComponent] = useState(null);
  const [clipboardSourcePositions, setClipboardSourcePositions] = useState(null); // 원본 위치 저장
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showPasteToast, setShowPasteToast] = useState(false);
  const [pasteOffsetCount, setPasteOffsetCount] = useState(0); // 연속 붙여넣기 시 오프셋 증가
  const [isClipboardFromCut, setIsClipboardFromCut] = useState(false); // 잘라내기 여부 추적
  
  // 지연된 작업 처리를 위한 상태 (즉시 실행 트리거용)
  const [pendingCutIds, setPendingCutIds] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  
  // 드래그 선택 상태
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [justFinishedSelecting, setJustFinishedSelecting] = useState(false);
  const dashboardContainerRef = useRef(null);
  // 자동 스크롤 상태 및 마지막 마우스 이벤트 저장
  const autoScrollRef = useRef({ rafId: null, active: false, directionY: 0, speed: 0 });
  const lastMouseEventRef = useRef({ clientX: 0, clientY: 0, ctrlKey: false, metaKey: false, shiftKey: false });
  // 선택 계산 타이밍 (단순화 후에도 남겨둘 수 있음)
  const lastComputeTsRef = useRef(0); // ms
  // setInterval 기반 심플 자동 스크롤
  const autoScrollTimerRef = useRef(null);
  // 실제 스크롤 타겟
  const scrollTargetRef = useRef({ type: 'element', el: null });
  
  // 그리드 세분화 상태 관리 (12, 24, 48, 96 등)
  const [gridColumns, setGridColumns] = useState(dashboardTemplate?.layout?.columns || 12);
  const [showGridChangeConfirm, setShowGridChangeConfirm] = useState(false);
  const [pendingGridColumns, setPendingGridColumns] = useState(null);
  const [isGridTransitioning, setIsGridTransitioning] = useState(false);

  // ============================================================================
  // 그리드 컬럼 수 동기화 (팝업 자동 크기 모드 지원)
  // ============================================================================
  // 우선순위:
  //   1순위: overrideGridColumns (팝업 자동 크기 모드에서 전달됨)
  //   2순위: dashboardTemplate.layout.columns (템플릿 설정값)
  //   3순위: 기본값 12
  // 
  // 팝업 자동 크기 모드일 때:
  //   - overrideGridColumns = 바운딩 박스 너비 (예: 6)
  //   - 결과: 차트들이 6칸 그리드에 꽉 차게 배치됨
  // ============================================================================
  useEffect(() => {
    // overrideGridColumns가 있으면 최우선 적용 (팝업 자동 크기 모드)
    if (overrideGridColumns && typeof overrideGridColumns === 'number') {
      setGridColumns(overrideGridColumns);
      return;
    }

    // 템플릿에서 설정된 컬럼 수 사용
    const tmplCols = dashboardTemplate?.layout?.columns;
    if (typeof tmplCols === 'number' && tmplCols > 0 && tmplCols !== gridColumns) {
      setGridColumns(tmplCols);
    }
  }, [dashboardTemplate?.layout?.columns, overrideGridColumns]);

  // 그리드 컬럼 변경 함수
  const handleGridColumnsChange = useCallback((newColumns) => {
    const oldColumns = gridColumns;
    const ratio = newColumns / oldColumns;
    
    // 모든 레이아웃 스케일링
    const scaledLayouts = {};
    Object.keys(layouts).forEach(breakpoint => {
      scaledLayouts[breakpoint] = layouts[breakpoint].map(item => {
        const minW = item.minW || 1;
        const minH = item.minH || 1;

        let nextW = Math.round(item.w * ratio);
        nextW = Math.max(minW, Math.min(nextW, newColumns));

        let nextX = Math.round(item.x * ratio);
        nextX = Math.max(0, Math.min(nextX, newColumns - nextW));

        let nextH = Math.round(item.h * ratio);
        nextH = Math.max(minH, nextH);

        let nextY = Math.round(item.y * ratio);
        nextY = Math.max(0, nextY);

        return {
          ...item,
          x: nextX,
          y: nextY,
          w: nextW,
          h: nextH
        };
      });
    });
    
    // 대시보드 템플릿의 컴포넌트 위치도 업데이트
    const updatedTemplate = {
      ...dashboardTemplate,
      layout: {
        ...dashboardTemplate.layout,
        columns: newColumns
      },
      components: dashboardTemplate.components.map(comp => {
        if (!comp.position) return comp;

        const currentX = comp.position.x || 0;
        const currentW = comp.position.w || 3;
        const currentY = comp.position.y || 0;
        const currentH = comp.position.h || 2;

        let nextW = Math.round(currentW * ratio);
        nextW = Math.max(1, Math.min(nextW, newColumns));

        let nextX = Math.round(currentX * ratio);
        nextX = Math.max(0, Math.min(nextX, newColumns - nextW));

        let nextH = Math.round(currentH * ratio);
        nextH = Math.max(1, nextH);

        let nextY = Math.round(currentY * ratio);
        nextY = Math.max(0, nextY);

        return {
          ...comp,
          position: {
            ...comp.position,
            x: nextX,
            y: nextY,
            w: nextW,
            h: nextH
          }
        };
      })
    };
    
    // 전환 애니메이션을 위해 일시적으로 그리드 숨기기
    setIsGridTransitioning(true);
    
    // 상태 업데이트
    setGridColumns(newColumns);
    setLayouts(scaledLayouts);
    setDashboardTemplateAndUpdateData(updatedTemplate);
    setShowGridChangeConfirm(false);
    setPendingGridColumns(null);
    setHasLayoutChanged(true);
    
    // 짧은 지연 후 그리드 다시 표시
    setTimeout(() => {
      setIsGridTransitioning(false);
    }, 100);
    
    showSuccessMessage(`그리드가 ${oldColumns}열에서 ${newColumns}열로 변경되었습니다.`);
  }, [gridColumns, layouts, setLayouts, dashboardTemplate, setDashboardTemplateAndUpdateData, setHasLayoutChanged]);

  // 편집 툴바 높이를 동적으로 측정하여 컨텐츠 상단 패딩을 정확히 맞춤
  const toolbarRef = useRef(null);
  const wrapperRef = useRef(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [wrapperTop, setWrapperTop] = useState(60);
  useLayoutEffect(() => {
    const measure = () => {
      try {
        const h = toolbarRef.current ? toolbarRef.current.offsetHeight : 0;
        setToolbarHeight(h || 0);
        // wrapperTop을 항상 60px로 고정 (앱 헤더 바로 아래)
        setWrapperTop(60);
      } catch (e) {
        setToolbarHeight(0);
        setWrapperTop(60);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [editMode]);
  
  // 드래그 선택 기능
  useEffect(() => {
    if (!editMode) return;
    
    // 선택 갱신 헬퍼
    const updateSelection = (endX, endY, modifierKeys = { ctrlKey: false, metaKey: false, shiftKey: false }) => {
      if (!selectionStart) return;
      const container = dashboardContainerRef.current;
      if (!container) return;

      // SelectionBox 즉시 갱신 (이벤트 당 1회)
      setSelectionEnd({ x: endX, y: endY });

      const selectionRect = {
        left: Math.min(selectionStart.x, endX),
        top: Math.min(selectionStart.y, endY),
        right: Math.max(selectionStart.x, endX),
        bottom: Math.max(selectionStart.y, endY)
      };

      const componentElements = container.querySelectorAll('.react-grid-item');
      const selectedIds = [];
      componentElements.forEach((element) => {
        const componentId = element.getAttribute('data-component-id');
        if (!componentId) return;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementLeft = elementRect.left - containerRect.left + container.scrollLeft;
        const elementTop = elementRect.top - containerRect.top + container.scrollTop;
        const elementRight = elementLeft + elementRect.width;
        const elementBottom = elementTop + elementRect.height;
        const overlaps = !(elementRight < selectionRect.left || elementLeft > selectionRect.right || elementBottom < selectionRect.top || elementTop > selectionRect.bottom);
        if (overlaps) selectedIds.push(componentId);
      });

      const isModifier = !!(modifierKeys.ctrlKey || modifierKeys.metaKey || modifierKeys.shiftKey);
      if (isModifier) {
        const baseSelected = (typeof window !== 'undefined' && window.__selectedComponentIds) ? window.__selectedComponentIds : selectedComponentIds;
        const newSelection = [...new Set([...(baseSelected || []), ...selectedIds])];
        selectComponents(newSelection);
      } else {
        selectComponents(selectedIds);
      }
    };

    const stopAutoScroll = () => {
      const ref = autoScrollRef.current;
      ref.active = false;
      if (autoScrollTimerRef.current) {
        try { clearInterval(autoScrollTimerRef.current); } catch (_) {}
        autoScrollTimerRef.current = null;
      }
      if (ref.rafId) { try { cancelAnimationFrame(ref.rafId); } catch (_) {} ref.rafId = null; }
    };

    const getScrollableTarget = () => {
      let el = dashboardContainerRef.current;
      if (!el) return { type: 'window', el: null };
      // 가장 가까운 스크롤 가능한 조상 찾기
      let cur = el;
      while (cur && cur.parentNode) {
        const canScroll = (cur.scrollHeight || 0) > (cur.clientHeight || 0);
        if (canScroll) return { type: 'element', el: cur };
        cur = cur.parentNode;
      }
      // 문서 스크롤로 폴백
      return { type: 'window', el: null };
    };

    const startAutoScroll = (directionY) => {
      const ref = autoScrollRef.current;
      const container = dashboardContainerRef.current;
      if (!container) return;
      if (ref.active && autoScrollTimerRef.current) return;
      ref.active = true;
      ref.directionY = directionY;
      // 고정 스텝(휠 유사) 스크롤
      const STEP_PX = 96; // tick 당 px (더 빠르게)
      const TICK_MS = 16; // 약 60fps
      // 스크롤 타겟 결정 (대시보드 컨테이너 또는 윈도우)
      scrollTargetRef.current = getScrollableTarget();
      autoScrollTimerRef.current = setInterval(() => {
        if (!autoScrollRef.current.active) return;
        const target = scrollTargetRef.current;
        if (target.type === 'window') {
          try { window.scrollBy(0, STEP_PX * autoScrollRef.current.directionY); } catch (_) {}
        } else if (target.el) {
          const c = target.el;
          const prev = c.scrollTop;
          const maxScroll = c.scrollHeight - c.clientHeight;
          const next = Math.max(0, Math.min(maxScroll, prev + (STEP_PX * autoScrollRef.current.directionY)));
          c.scrollTop = next;
        }
        // 시각적 박스만 보정 (겹침 계산은 mousemove에서만)
        const lme = lastMouseEventRef.current;
        const rect = container.getBoundingClientRect();
        const endX = lme.clientX - rect.left + container.scrollLeft;
        const endY = lme.clientY - rect.top + container.scrollTop;
        setSelectionEnd({ x: endX, y: endY });
      }, TICK_MS);
    };
    
    const handleMouseDown = (e) => {
      // 빈 공간에서 마우스를 누른 경우만 드래그 선택 시작
      // 차트나 다른 요소를 클릭한 경우는 제외
      const target = e.target;
      const isDashboardContainer = target.classList.contains('dashboard-container') || 
                                   target.classList.contains('react-grid-layout') ||
                                   target.classList.contains('grid-dots-overlay') ||
                                   (target.closest('.react-grid-layout') && !target.closest('.react-grid-item'));
      const isComponent = target.closest('.react-grid-item');
      
      // 빈 공간 클릭 시 드래그 선택 시작 (Shift/Ctrl 키 상관없이)
      // 단, 컴포넌트나 툴바 등을 클릭한 경우는 제외
      if (isDashboardContainer && !isComponent && !target.closest('.dashboard-edit-toolbar')) {
        e.preventDefault();
        const container = dashboardContainerRef.current;
        if (!container) return;
        
        const rect = container.getBoundingClientRect();
        const startX = e.clientX - rect.left + container.scrollLeft;
        const startY = e.clientY - rect.top + container.scrollTop;
        
        setIsSelecting(true);
        setSelectionStart({ x: startX, y: startY });
        setSelectionEnd({ x: startX, y: startY });
        lastMouseEventRef.current = { clientX: e.clientX, clientY: e.clientY, ctrlKey: !!e.ctrlKey, metaKey: !!e.metaKey, shiftKey: !!e.shiftKey };
        // 드래그 시작 시 스크롤 타겟 재계산
        scrollTargetRef.current = getScrollableTarget();
        
        // 기존 선택 해제 (Ctrl/Shift 누르지 않은 경우)
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
          clearSelection();
        }
        
      }
    };
    
    const handleMouseMove = (e) => {
      if (!isSelecting || !selectionStart) return;
      
      const container = dashboardContainerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const endX = e.clientX - rect.left + container.scrollLeft;
      const endY = e.clientY - rect.top + container.scrollTop;
      lastMouseEventRef.current = { clientX: e.clientX, clientY: e.clientY, ctrlKey: !!e.ctrlKey, metaKey: !!e.metaKey, shiftKey: !!e.shiftKey };
      updateSelection(endX, endY, e);

      // 심플 오버플로우 자동 스크롤 (마우스 휠처럼 일정 속도)
      const isOverflowTop = e.clientY < rect.top;
      const isOverflowBottom = e.clientY > rect.bottom;
      if (isOverflowTop) { startAutoScroll(-1); return; }
      if (isOverflowBottom) { startAutoScroll(1); return; }
      stopAutoScroll();
    };
    
    const handleMouseUp = (e) => {
      if (isSelecting) {
        setIsSelecting(false);
        setSelectionStart(null);
        setSelectionEnd(null);
        // 자동 스크롤 중지
        stopAutoScroll();
        
        // 캐시 정리 (사용 안 함)
        
        // 플래그를 설정하여 onClick 이벤트가 선택을 해제하지 않도록 함
        setJustFinishedSelecting(true);
        setTimeout(() => setJustFinishedSelecting(false), 100);
      }
    };
    
    // 이벤트 리스너 등록
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      // 정리 시 자동 스크롤 중지
      stopAutoScroll();
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [editMode, isSelecting, selectionStart, clearSelection, selectComponents]);
  
  // 키보드 이벤트 핸들러 (복사/붙여넣기/잘라내기/삭제)
  useEffect(() => {
    if (!editMode) return;
    
    const handleKeyDown = (e) => {
      // 모달이 열려 있으면 전역 단축키 비활성화 (모달 내부의 기본 동작만 허용)
      if (showOptionsModal || showNLComponentModal || showConflictModal || showBackgroundModal) {
        return;
      }
      
      // 입력/에디터 포커스 시 전역 단축키 비활성화
      try {
        const activeEl = document.activeElement;
        const tag = (activeEl && activeEl.tagName) ? activeEl.tagName.toLowerCase() : '';
        const isEditableTag = tag === 'input' || tag === 'textarea' || tag === 'select';
        const isContentEditable = !!(activeEl && activeEl.isContentEditable);
        const hasTextboxRole = !!(activeEl && activeEl.getAttribute && activeEl.getAttribute('role') === 'textbox');
        const insideMonaco = !!(activeEl && activeEl.closest && activeEl.closest('.monaco-editor'));
        const insideTemplateEditor = !!(activeEl && activeEl.closest && (activeEl.closest('.dashboard-options-editor-content') || activeEl.closest('.template-editor-container')));
        if (isEditableTag || isContentEditable || hasTextboxRole || insideMonaco || insideTemplateEditor) {
          return; // 텍스트/에디터 컨텍스트에서는 대시보드 전역 복사/붙여넣기 비활성화
        }
      } catch (_) {}
      
      // Ctrl+A 또는 Cmd+A (모두 선택)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        // 모든 차트 컴포넌트 선택
        const allComponentIds = dashboardTemplate?.components?.map(c => c.id) || [];
        if (allComponentIds.length > 0) {
          selectComponents(allComponentIds);
        }
      }
      
      // Ctrl+C 또는 Cmd+C (복사)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedComponentIds.length > 0) {
        e.preventDefault();
        // 다중 선택된 컴포넌트들을 모두 복사
        const componentsToCopy = dashboardTemplate?.components?.filter(c => 
          selectedComponentIds.includes(c.id)
        );
        if (componentsToCopy && componentsToCopy.length > 0) {
          // 여러 컴포넌트 복사를 위해 배열로 저장
          setClipboardComponent(componentsToCopy.length === 1 ? componentsToCopy[0] : componentsToCopy);
          // 원본 위치 저장
          setClipboardSourcePositions(componentsToCopy.map(c => ({
            id: c.id,
            position: c.position
          })));
          setPasteOffsetCount(0); // 오프셋 카운터 리셋
          setIsClipboardFromCut(false); // 복사 작업 표시
          setShowCopyToast(true);
          setTimeout(() => setShowCopyToast(false), 2000);
        }
      }
      
      // Ctrl+X 또는 Cmd+X (잘라내기)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x' && selectedComponentIds.length > 0) {
        e.preventDefault();
        const componentsToCut = dashboardTemplate?.components?.filter(c => 
          selectedComponentIds.includes(c.id)
        );
        if (componentsToCut && componentsToCut.length > 0) {
          if (savePreActionState) {
            const desc = `차트 잘라내기: ${selectedComponentIds.join(', ')}`;
            savePreActionState('cut_components', selectedComponentIds[0], desc);
          }
          // 여러 컴포넌트 잘라내기를 위해 배열로 저장
          setClipboardComponent(componentsToCut.length === 1 ? componentsToCut[0] : componentsToCut);
          // 원본 위치 저장
          setClipboardSourcePositions(componentsToCut.map(c => ({
            id: c.id,
            position: c.position
          })));
          setPasteOffsetCount(0); // 오프셋 카운터 리셋
          setIsClipboardFromCut(true); // 잘라내기 작업 표시
          // setState를 사용하여 즉시 useEffect 트리거
          setPendingCutIds(selectedComponentIds);
          clearSelection();
        }
      }
      
      // Ctrl+V 또는 Cmd+V (붙여넣기)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboardComponent) {
        e.preventDefault();
        
        // 배열인지 단일 컴포넌트인지 확인
        const componentsToPaste = Array.isArray(clipboardComponent) ? clipboardComponent : [clipboardComponent];
        const timestamp = Date.now();
        
        // 복사된 컴포넌트들 간의 상대 위치 계산을 위한 최소값 먼저 구하기
        // (다중 복사 시 레이아웃 유지를 위해 필요)
        let minX = Infinity, minY = Infinity;
        componentsToPaste.forEach(comp => {
          minX = Math.min(minX, comp.position?.x || 0);
          minY = Math.min(minY, comp.position?.y || 0);
        });
        
        // 붙여넣기 위치 계산 
        let baseX = 0, baseY = 0;
        
        
        if (clipboardSourcePositions && clipboardSourcePositions.length > 0) {
          // 잘라내기인 경우: 원본 위치 그대로, 복사인 경우: 오프셋 적용
          const offset = isClipboardFromCut ? 0 : (pasteOffsetCount + 1) * 1; // 복사일 때만 오프셋
          
          // 원본 위치들의 최소값 찾기
          let sourceMinX = Infinity, sourceMinY = Infinity;
          clipboardSourcePositions.forEach(pos => {
            sourceMinX = Math.min(sourceMinX, pos.position?.x || 0);
            sourceMinY = Math.min(sourceMinY, pos.position?.y || 0);
          });
          
          // 기본 위치: 원본 위치에서 오프셋만큼 이동
          baseX = sourceMinX + offset;
          baseY = sourceMinY + offset;
          
          
          // 그리드 경계 체크 (화면 밖으로 나가지 않도록)
          const maxWidth = Math.max(...componentsToPaste.map(c => (c.position?.x || 0) + (c.position?.w || 3) - minX));
          
          // 그리드 컬럼 수를 고려한 경계 체크
          const gridCols = gridColumns || 12;
          if (baseX + maxWidth > gridCols) {
            // 오른쪽 경계를 넘으면 다음 줄로
            baseX = offset;
            baseY = sourceMinY + offset + 3; // 3칸 아래로
          }
          
          // 간단한 겹침 체크 - 복사일 때만, 정확히 같은 위치에만 있을 때만 이동
          let needsRepositioning = false;
          
          // 잘라내기인 경우 겹침 체크 건너뜀 (원본이 이미 제거됨)
          // 복사인 경우에만 겹침 체크
          if (!isClipboardFromCut && dashboardTemplate.components && dashboardTemplate.components.length > 0) {
            for (let paste of componentsToPaste) {
              const pasteX = baseX + ((paste.position?.x || 0) - minX);
              const pasteY = baseY + ((paste.position?.y || 0) - minY);
              
              // 정확히 같은 위치에 컴포넌트가 있는지만 체크
              const hasExactOverlap = dashboardTemplate.components.some(existing => {
                const existX = existing.position?.x || 0;
                const existY = existing.position?.y || 0;
                return existX === pasteX && existY === pasteY;
              });
              
              if (hasExactOverlap) {
                needsRepositioning = true;
                break;
              }
            }
          }
          
          // 정확히 같은 위치에 겹치는 경우에만 아래로 이동
          if (needsRepositioning) {
            // 조금 더 오프셋을 늘려서 재시도
            baseX = sourceMinX + offset + 1;
            baseY = sourceMinY + offset + 1;
            
            // 그래도 경계를 넘으면 맨 아래로
            if (baseX + maxWidth > gridCols) {
              const maxY = Math.max(
                0,
                ...dashboardTemplate.components.map(c => (c.position?.y || 0) + (c.position?.h || 2))
              );
              baseX = 0;
              baseY = maxY + 1;
            }
          }
        } else {
          // 원본 위치 정보가 없으면 맨 아래 배치
          const maxY = Math.max(
            0,
            ...dashboardTemplate.components.map(c => (c.position?.y || 0) + (c.position?.h || 2))
          );
          baseY = maxY + 1;
        }
        
        // minX, minY는 이미 위에서 계산되었음
        const newComponents = [];
        const newIds = [];
        
        componentsToPaste.forEach((component, index) => {
          const newId = `${component.id}_copy_${timestamp}_${index}`;
          newIds.push(newId);
          
          // 상대 위치 유지하면서 새 위치 계산
          const relativeX = (component.position?.x || 0) - minX;
          const relativeY = (component.position?.y || 0) - minY;
          
          const newComponent = {
            ...component,
            id: newId,
            position: {
              x: baseX + relativeX,
              y: baseY + relativeY,
              w: component.position?.w || 3,
              h: component.position?.h || 2
            },
            title: `${component.title || '차트'} (복사본)`
          };
          
          newComponents.push(newComponent);
          
          // 레이아웃에도 추가
          addComponentToLayout({
            id: newId,
            position: {
              x: baseX + relativeX,
              y: baseY + relativeY,
              w: component.position?.w || 3,
              h: component.position?.h || 2
            }
          }, true /* skipHistorySaving */, false /* forceBottomPlacement */);
        });
        
        // 복사 작업인 경우에만 연속 붙여넣기 카운터 증가
        if (!isClipboardFromCut) {
          setPasteOffsetCount(pasteOffsetCount + 1);
        } else {
          // 잘라내기 후 첫 붙여넣기가 완료되면 이제는 복사로 취급
          setIsClipboardFromCut(false);
        }
        
        // 붙여넣기 전 상태를 Undo 히스토리에 저장 (전체 스냅샷)
        if (savePreActionState) {
          const desc = componentsToPaste.length === 1 
            ? `차트 붙여넣기: ${componentsToPaste[0].id}`
            : `차트 ${componentsToPaste.length}개 붙여넣기`;
          savePreActionState('paste_components', newIds[0], desc);
        }

        // 모든 새 컴포넌트 추가
        const updatedTemplate = {
          ...dashboardTemplate,
          components: [...dashboardTemplate.components, ...newComponents]
        };
        
        setDashboardTemplateAndUpdateData(updatedTemplate);
        
        // 새로 추가된 컴포넌트들 선택
        selectComponents(newIds);
        
        // 새로 붙여넣은 첫 번째 컴포넌트 위치로 스크롤
        scrollToNewComponent(newComponents[0].position, dashboardTemplate);
        
        setShowPasteToast(true);
        setTimeout(() => setShowPasteToast(false), 2000);
      }
      
      // Delete 또는 Backspace (삭제)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedComponentIds.length > 0) {
        e.preventDefault();
        if (savePreActionState) {
          const desc = selectedComponentIds.length === 1 
            ? `차트 삭제: ${selectedComponentIds[0]}`
            : `차트 ${selectedComponentIds.length}개 삭제`;
          savePreActionState('delete_components', selectedComponentIds[0], desc);
        }
        // setState를 사용하여 즉시 useEffect 트리거
        setPendingDeleteIds(selectedComponentIds);
        clearSelection();
      }
      
      // ESC 키 (선택 해제)
      if (e.key === 'Escape') {
        clearSelection();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, selectedComponentIds, clipboardComponent, clipboardSourcePositions, pasteOffsetCount, setPasteOffsetCount, gridColumns, dashboardTemplate, setDashboardTemplateAndUpdateData, addComponentToLayout, clearSelection, savePreActionState, selectComponents, showOptionsModal, showNLComponentModal, showConflictModal, showBackgroundModal]);

  // 저장/취소/저장없이나가기 - 배경 커밋 제어 래퍼 (조기 반환보다 위에서 훅 선언)
  const handleSaveAndExitWithBackground = useCallback(async () => {
    try {
      const currentDashboardId = dashboardTemplate?.dashboardId || dashboardTemplate?.dashboard_id;
      if (pendingBackground && currentDashboardId) {
        if (pendingBackground.removeFlag) {
          const response = await axios.delete(
            `/api/visualization/dashboards/${currentDashboardId}/background/remove/`,
            { withCredentials: true }
          );
          const updated = response?.data?.dashboard;
          if (updated) {
            setDashboardTemplateAndUpdateData({
              ...dashboardTemplate,
              background_image_url: updated.background_image_url,
              background_opacity: updated.background_opacity,
              background_position: updated.background_position,
              background_size: updated.background_size
            });
          }
        } else {
          const formData = new FormData();
          if (pendingBackground.imageChanged && pendingBackground.file) {
            formData.append('image', pendingBackground.file);
          }
          if (typeof pendingBackground.background_opacity !== 'undefined') {
            formData.append('background_opacity', pendingBackground.background_opacity);
          }
          if (pendingBackground.background_position) {
            formData.append('background_position', pendingBackground.background_position);
          }
          if (pendingBackground.background_size) {
            formData.append('background_size', pendingBackground.background_size);
          }
          const response = await axios.post(
            `/api/visualization/dashboards/${currentDashboardId}/background/`,
            formData,
            { withCredentials: true }
          );
          const updated = response?.data?.dashboard;
          if (updated) {
            setDashboardTemplateAndUpdateData({
              ...dashboardTemplate,
              background_image_url: updated.background_image_url,
              background_opacity: updated.background_opacity,
              background_position: updated.background_position,
              background_size: updated.background_size
            });
          }
        }
      }
    } catch (e) {
      console.error('[Dashboard] 배경 업로드/제거 커밋 실패:', e);
    } finally {
      if (pendingBackground) setPendingBackground(null);
    }
    await saveAndExit();
  }, [pendingBackground, dashboardTemplate, setDashboardTemplateAndUpdateData, saveAndExit]);

  const handleCancelChanges = useCallback(() => {
    if (pendingBackground) setPendingBackground(null);
    if (cancelChanges) cancelChanges();
  }, [pendingBackground, cancelChanges]);

  const handleExitWithoutSaving = useCallback(() => {
    if (pendingBackground) setPendingBackground(null);
    if (exitWithoutSaving) exitWithoutSaving();
  }, [pendingBackground, exitWithoutSaving]);

  
  
  // 드래그 상태 관리
  const [isDragging, setIsDragging] = useState(false);
  
  // 대시보드 충돌 모달 관련 상태 추가
  const [conflictSaving, setConflictSaving] = useState(false);
  
  // 텍스트 라벨 추가 함수
  // 상단 툴바 버튼을 통한 텍스트 라벨 추가 기능은 제거됨 (차트 추가에서 처리)
  
  // 텍스트 라벨 전용 편집/미리보기 함수 제거 (공용 모달 사용)
  
  // 대시보드 컨텍스트 사용 (먼저 선언 - 다른 함수들이 사용하므로)
  const { 
    registerDashboardActions, 
    registerEditModeActions,
    updateDashboardTemplate,
    unregisterDashboardActions,
    unregisterEditModeActions,
    unregisterDashboardTemplate,
    dashboardListActions,
    setCurrentDashboardId,
    dashboardList,  // 대시보드 목록 추가
    isDashboardListLoading  // 대시보드 목록 로딩 상태 추가
  } = useDashboardContext();
  
  // dashboardListActions에서 refreshDashboardList 추출
  // 이제 DashboardSelector에서 직접 window.refreshDashboardList를 관리하므로 여기서는 제거
  const refreshDashboardList = dashboardListActions?.refreshDashboardList;

  // 전용 텍스트 라벨 편집 전역 함수 제거 (공용 모달 사용)

  // dashboardTemplate을 위한 ref 추가 (최신 값 참조용)
  const dashboardTemplateRef = useRef(dashboardTemplate);
  useEffect(() => {
    dashboardTemplateRef.current = dashboardTemplate;
  }, [dashboardTemplate]);

  // z-순서 변경 핸들러 등록 (window.onZOrderAction)
  useEffect(() => {
    window.onZOrderAction = (componentId, action) => {
      const currentTemplate = dashboardTemplateRef.current; // 최신 템플릿 참조
      if (!editMode || !currentTemplate || !currentTemplate.components) return;
      
      // ⭐ 그룹 소속 확인
      const groupId = getComponentGroup(componentId);
      
      let componentsToMove = [componentId];
      
      // 그룹에 속한 경우 그룹 전체를 함께 이동
      if (groupId) {
        componentsToMove = getGroupComponentIds(currentTemplate, groupId);
      }
      
      const components = [...currentTemplate.components];
      
      // 이동할 컴포넌트들의 현재 인덱스 찾기
      const moveIndices = componentsToMove
        .map(id => components.findIndex(c => c.id === id))
        .filter(idx => idx !== -1)
        .sort((a, b) => a - b); // 인덱스 오름차순 정렬
      
      if (moveIndices.length === 0) return;
      
      // 목표 인덱스 계산 (첫 번째 컴포넌트 기준)
      const firstIdx = moveIndices[0];
      let targetIdx = firstIdx;
      
      switch(action) {
        case 'bringToFront': 
          targetIdx = components.length - moveIndices.length; 
          break;
        case 'bringForward': 
          targetIdx = Math.min(components.length - moveIndices.length, firstIdx + 1); 
          break;
        case 'sendBackward': 
          targetIdx = Math.max(0, firstIdx - 1); 
          break;
        case 'sendToBack': 
          targetIdx = 0; 
          break;
      }
      
      if (targetIdx !== firstIdx) {
        // 이동할 컴포넌트들을 배열에서 제거 (역순으로 제거하여 인덱스 문제 방지)
        const movedComponents = [];
        for (let i = moveIndices.length - 1; i >= 0; i--) {
          const [component] = components.splice(moveIndices[i], 1);
          movedComponents.unshift(component); // 원래 순서 유지
        }
        
        // 목표 위치에 삽입
        components.splice(targetIdx, 0, ...movedComponents);
        
        console.log('[onZOrderAction] z-order 변경 완료:', {
          action,
          from: firstIdx,
          to: targetIdx,
          movedCount: movedComponents.length
        });
        
        // z-order 변경 중 플래그 설정
        window._isZOrderChanging = true;
        setDashboardTemplateAndUpdateData({ ...currentTemplate, components });
        setHasLayoutChanged(true);
        
        // 레이아웃 변경 이벤트 처리 대기 후 플래그 해제
        setTimeout(() => {
          window._isZOrderChanging = false;
        }, 1000);
      }
    };
    return () => { window.onZOrderAction = undefined; };
  }, [editMode, setDashboardTemplateAndUpdateData, setHasLayoutChanged, getComponentGroup]);

  // 기존 대시보드 덮어쓰기 처리
  const handleReplaceExisting = useCallback(async () => {
    if (!conflictTemplate) return;
    
    setConflictSaving(true);
    try {
      // 현재 편집 중인 대시보드 ID 가져오기
      const currentDashboardId = dashboardTemplate?.dashboardId || dashboardTemplate?.dashboard_id;
      const response = await DashboardService.saveDashboardWithOptions(
        conflictTemplate,
        { 
          force_replace: true,
          current_dashboard_id: currentDashboardId
        }
      );
      
      if (response.success) {
        const finalTemplate = response.data?.dashboard_template || conflictTemplate;
        
        // 덮어쓴 대시보드의 ID가 현재 대시보드와 다른 경우 해당 대시보드로 전환
        const replacedDashboardId = finalTemplate.dashboard_id || finalTemplate.dashboardId;
        if (replacedDashboardId !== currentDashboardId && reloadDashboardTemplate) {
          await reloadDashboardTemplate(replacedDashboardId);
        } else {
          // 같은 대시보드인 경우 로컬 상태만 업데이트
          setDashboardTemplateAndUpdateData(finalTemplate);
        }
        
        closeConflictModal();
      } else {
        console.error('[Dashboard] 기존 대시보드 덮어쓰기 실패:', response.error);
      }
    } catch (error) {
      console.error('[Dashboard] 기존 대시보드 덮어쓰기 중 오류:', error);
    } finally {
      setConflictSaving(false);
    }
  }, [conflictTemplate, setDashboardTemplateAndUpdateData, closeConflictModal, reloadDashboardTemplate]);

  // 새로운 대시보드 생성 처리
  const handleCreateNew = useCallback(async () => {
    if (!conflictTemplate) return;
    
    setConflictSaving(true);
    try {
      // 현재 편집 중인 대시보드 ID 가져오기
      const currentDashboardId = dashboardTemplate?.dashboardId || dashboardTemplate?.dashboard_id;
      const response = await DashboardService.saveDashboardWithOptions(
        conflictTemplate,
        { 
          generate_new_id: true,
          current_dashboard_id: currentDashboardId
        }
      );
      
      if (response.success) {
        const finalTemplate = response.data?.dashboard_template || conflictTemplate;
        
        // 1. 먼저 대시보드 목록을 갱신
        if (refreshDashboardList) {
          await refreshDashboardList();
        }
        
        // 2. 새로 생성된 대시보드 ID로 재로드
        const newDashboardId = finalTemplate.dashboard_id || finalTemplate.dashboardId;
        if (newDashboardId && reloadDashboardTemplate) {
          await reloadDashboardTemplate(newDashboardId);
        } else {
          // reloadDashboardTemplate이 없으면 기존 방식 사용
          setDashboardTemplateAndUpdateData(finalTemplate);
        }
        
        closeConflictModal();
      } else {
        console.error('[Dashboard] 새로운 대시보드 생성 실패:', response.error);
      }
    } catch (error) {
      console.error('[Dashboard] 새로운 대시보드 생성 중 오류:', error);
    } finally {
      setConflictSaving(false);
    }
  }, [conflictTemplate, setDashboardTemplateAndUpdateData, closeConflictModal, reloadDashboardTemplate]);

  // 기존 대시보드 삭제 후 새 대시보드로 이동
  const handleDeleteOriginal = useCallback(async () => {
    if (!conflictInfo?.originalDashboardId || !conflictInfo?.newDashboardId) return;
    
    setConflictSaving(true);
    try {
      // 1. 기존 대시보드 삭제
      const deleteResponse = await DashboardService.deleteDashboard(conflictInfo.originalDashboardId);
      
      if (deleteResponse.success) {
        // 2. 대시보드 목록 갱신
        if (refreshDashboardList) {
          await refreshDashboardList();
        }
        
        // 3. 새 대시보드로 이동
        if (reloadDashboardTemplate) {
          await reloadDashboardTemplate(conflictInfo.newDashboardId);
        } else {
          // 새 템플릿으로 로컬 상태 업데이트
          setDashboardTemplateAndUpdateData(conflictInfo.newTemplate);
        }
        
        // 4. 현재 대시보드 ID 업데이트
        if (setCurrentDashboardId) {
          setCurrentDashboardId(conflictInfo.newDashboardId);
        }
        
        closeConflictModal();
      } else {
        console.error('[Dashboard] 기존 대시보드 삭제 실패:', deleteResponse.error);
        showErrorMessage(`기존 대시보드 삭제 실패: ${deleteResponse.error}`);
      }
    } catch (error) {
      console.error('[Dashboard] 기존 대시보드 삭제 중 오류:', error);
      showErrorMessage('기존 대시보드 삭제 중 오류가 발생했습니다.');
    } finally {
      setConflictSaving(false);
    }
  }, [conflictInfo, closeConflictModal, reloadDashboardTemplate, setDashboardTemplateAndUpdateData]);

  // 기존 대시보드 유지하고 새 대시보드로 이동
  const handleKeepBoth = useCallback(async () => {
    if (!conflictInfo?.newDashboardId) return;
    
    setConflictSaving(true);
    try {
      // 1. 대시보드 목록 갱신
      if (refreshDashboardList) {
        await refreshDashboardList();
      }
      
      // 2. 새 대시보드로 이동
      if (reloadDashboardTemplate) {
        await reloadDashboardTemplate(conflictInfo.newDashboardId);
      } else {
        // 새 템플릿으로 로컬 상태 업데이트
        setDashboardTemplateAndUpdateData(conflictInfo.newTemplate);
      }
      
      // 3. 현재 대시보드 ID 업데이트
      if (setCurrentDashboardId) {
        setCurrentDashboardId(conflictInfo.newDashboardId);
      }
      
      closeConflictModal();
    } catch (error) {
      console.error('[Dashboard] 새 대시보드로 이동 중 오류:', error);
      showErrorMessage('새 대시보드로 이동 중 오류가 발생했습니다.');
    } finally {
      setConflictSaving(false);
    }
  }, [conflictInfo, closeConflictModal, reloadDashboardTemplate, setDashboardTemplateAndUpdateData]);

  
  // 텍스트 라벨 전용 저장/닫기 로직 제거 (공용 모달 사용)

  // Use the custom hook for dashboard actions
  const {
    handleDashboardOptionsSave,
    handleComponentCreated,
    handleTemplateSaved,
    handleComponentDoubleClick,
    deleteComponent,
    addNewChart,
    addComponentToLocalState
  } = useDashboardActions({
    dashboardTemplate,
    setDashboardTemplateAndUpdateData,
    layouts,
    setLayouts,
    addComponentToLayout,
    removeComponentFromLayout,
    toggleEditMode,
    editMode,
    setHasLayoutChanged,
    componentToUpdate,
    closeNLComponentModal,
    openNLComponentModal,
    // 🔄 Undo/Redo 기능을 위한 추가 파라미터
    savePreActionState,
    // 🔄 히스토리 보존 편집 모드 진입
    enableEditModeWithHistoryPreservation,
    enableEditModeWithoutBackup,
    // 충돌 모달 관련 함수 추가
    openConflictModal
  });

  // 지연된 삭제/잘라내기 작업 즉시 처리
  useEffect(() => {
    if (pendingDeleteIds.length > 0 && deleteComponent && dashboardTemplate) {
      // 한 번에 모든 컴포넌트 삭제 (forEach 대신 필터링으로 처리)
      const updatedComponents = dashboardTemplate.components.filter(
        comp => !pendingDeleteIds.includes(comp.id)
      );
      
      const updatedTemplate = {
        ...dashboardTemplate,
        components: updatedComponents
      };
      
      // 템플릿 업데이트
      setDashboardTemplateAndUpdateData(updatedTemplate);
      
      // 레이아웃에서도 제거
      pendingDeleteIds.forEach(id => {
        removeComponentFromLayout(id, true /* skipHistorySaving */);
      });
      
      // 레이아웃 변경 플래그 설정
      if (setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
      setPendingDeleteIds([]); // 처리 후 초기화
    }
  }, [pendingDeleteIds, deleteComponent, dashboardTemplate, setDashboardTemplateAndUpdateData, removeComponentFromLayout, setHasLayoutChanged]);
  
  useEffect(() => {
    if (pendingCutIds.length > 0 && deleteComponent && dashboardTemplate) {
      // 한 번에 모든 컴포넌트 삭제 (forEach 대신 필터링으로 처리)
      const updatedComponents = dashboardTemplate.components.filter(
        comp => !pendingCutIds.includes(comp.id)
      );
      
      const updatedTemplate = {
        ...dashboardTemplate,
        components: updatedComponents
      };
      
      // 템플릿 업데이트
      setDashboardTemplateAndUpdateData(updatedTemplate);
      
      // 레이아웃에서도 제거
      pendingCutIds.forEach(id => {
        removeComponentFromLayout(id, true /* skipHistorySaving */);
      });
      
      // 레이아웃 변경 플래그 설정
      if (setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
      setPendingCutIds([]); // 처리 후 초기화
    }
  }, [pendingCutIds, deleteComponent, dashboardTemplate, setDashboardTemplateAndUpdateData, removeComponentFromLayout, setHasLayoutChanged]);

  // 컨텍스트 등록을 위한 초기화 플래그
  const [contextInitialized, setContextInitialized] = useState(false);

  // 컨텍스트 등록 - 마운트 시 한 번만 실행 (팝업 모드에서는 전역 컨텍스트에 등록하지 않음)
  useEffect(() => {
    if (!contextInitialized && !isPopup) {
      const dashboardActions = {
        handleComponentCreated,
        deleteComponent,
        addNewChart,
        addComponentToLocalState,
        handleDashboardOptionsSave,
        handleTemplateSaved,
        refreshChartData,
        reloadDashboardTemplate // 새로운 함수 추가
      };

      registerDashboardActions(dashboardActions);
      setContextInitialized(true);
    }

    // 컴포넌트 언마운트 시 액션 해제
    return () => {
      if (contextInitialized && !isPopup) {
        unregisterDashboardActions();
        unregisterEditModeActions();
        unregisterDashboardTemplate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextInitialized]); // contextInitialized만 의존성으로 사용
  
  // 편집 모드 액션 별도 등록 - editMode가 변경될 때마다 업데이트
  useEffect(() => {
    if (!isPopup) {
      const editModeActions = {
        editMode,
        layouts,
        toggleEditMode,
        openNLComponentModal,
        closeNLComponentModal,
        openOptionsModal,
        closeOptionsModal,
        backupCurrentTemplate,
        backupSpecificTemplate,
        enableEditModeWithoutBackup
      };

      registerEditModeActions(editModeActions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, isPopup]); // editMode 변경 시마다 업데이트

  // 대시보드 템플릿 컨텍스트 동기화
  // 템플릿이 변경될 때마다 컨텍스트 업데이트
  useEffect(() => {
    // 팝업 모드에서는 전역 컨텍스트의 템플릿을 변경하지 않음
    if (dashboardTemplate && contextInitialized && !isPopup) {
      updateDashboardTemplate(dashboardTemplate);
    }
  }, [dashboardTemplate, contextInitialized, updateDashboardTemplate]);

  // 페이지 타이틀 동적 설정 (테넌트 정보 표시)
  useEffect(() => {
    if (dashboardTemplate?.title) {
      document.title = `${dashboardTemplate.title} - Enerwiser`;
    } else {
      document.title = 'Enerwiser 분석 서비스';
    }
  }, [dashboardTemplate?.title]);

  // 팝업 모드에서 탭 변경 이벤트 처리
  useEffect(() => {
    if (!isPopup) return;

    const handleTabChange = (event) => {
      // 차트 리사이즈 이벤트 트리거
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        // 모든 차트 컴포넌트에 리사이즈 신호 전송
        const chartContainers = document.querySelectorAll('.dashboard-component');
        chartContainers.forEach(container => {
          const resizeEvent = new CustomEvent('componentResize', { 
            detail: { forced: true } 
          });
          container.dispatchEvent(resizeEvent);
        });
      }, 150);
    };

    // 탭 변경 이벤트 리스너 등록
    window.addEventListener('dashboardTabChanged', handleTabChange);
    
    // 일반 리사이즈 이벤트도 처리
    const handleResize = () => {
      // 차트 컴포넌트들에 리사이즈 신호 전송
      const chartContainers = document.querySelectorAll('.dashboard-component');
      chartContainers.forEach(container => {
        const resizeEvent = new CustomEvent('componentResize', { 
          detail: { forced: true } 
        });
        container.dispatchEvent(resizeEvent);
      });
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('dashboardTabChanged', handleTabChange);
      window.removeEventListener('resize', handleResize);
    };
  }, [isPopup]);

  // 레이아웃 초기화 - 템플릿 ID 변경 시에만 실행
  const prevTemplateIdRef = useRef(null);
  const layoutSyncedRef = useRef(false);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  
  useEffect(() => {
    if (dashboardTemplate && typeof dashboardTemplate === 'object' && dashboardTemplate.components) {
      const currentTemplateId = dashboardTemplate.dashboard_id || dashboardTemplate.dashboardId;
      
      // ========================================================================
      // 레이아웃 초기화 로직 (팝업 자동 크기 모드 지원)
      // ========================================================================
      // 
      // 우선순위:
      //   1순위: overrideLayout (팝업 자동 크기 모드에서 전달되는 정규화된 레이아웃)
      //   2순위: 템플릿의 position 정보 (일반 모드)
      // 
      // 팝업 자동 크기 모드일 때:
      //   - overrideLayout에 차트들의 (0,0) 기준 좌표가 들어있음
      //   - 템플릿의 원래 position은 무시하고 overrideLayout 사용
      //   - 결과: 차트들이 팝업 좌측 상단부터 빈틈없이 배치됨
      // ========================================================================
      
      // 템플릿 ID가 변경되었을 때만 레이아웃 초기화
      // 같은 템플릿의 업데이트(저장 후 등)는 레이아웃을 재초기화하지 않음
      if (currentTemplateId !== prevTemplateIdRef.current) {
        const componentsArray = dashboardTemplate.components.filter(comp => comp && comp.id);
        const gridLayout = componentsArray.map(comp => {
          // [우선순위 1] overrideLayout 적용 (팝업 자동 크기 모드)
          if (overrideLayout && Array.isArray(overrideLayout)) {
            const overrideItem = overrideLayout.find(item => item.i === comp.id);
            if (overrideItem) {
              // 정규화된 레이아웃 사용 (0,0 기준 좌표)
              return { ...overrideItem, i: comp.id, minW: 2, minH: 2 };
            }
          }

          // [우선순위 2] 템플릿 position 사용 (일반 모드)
          const layoutItem = {
            i: comp.id,
            x: comp.position?.x || 0,
            y: comp.position?.y || 0,
            w: comp.position?.w || 3,
            h: comp.position?.h || 2,
            minW: 2,
            minH: 2
          };
          
          return layoutItem;
        });
        
        // overrideLayout 추가 항목 처리
        if (overrideLayout && Array.isArray(overrideLayout)) {
           overrideLayout.forEach(item => {
               if (!gridLayout.find(g => g.i === item.i)) {
                   gridLayout.push(item);
               }
           });
        }
        
        // 모든 브레이크포인트에 동일한 레이아웃 설정
        setLayouts({ 
          lg: gridLayout,
          md: gridLayout,
          sm: gridLayout,
          xs: gridLayout,
          xxs: gridLayout
        });
        
        componentsArray.forEach(c => {
          const layoutItem = gridLayout.find(g => g.i === c.id);
        });
        
        prevTemplateIdRef.current = currentTemplateId;
        layoutSyncedRef.current = true;
        setIsLayoutReady(true);
      } else if (!layoutSyncedRef.current) {
        // 첫 로드 시 (템플릿 ID는 같지만 아직 동기화 안 된 경우)
        const componentsArray = dashboardTemplate.components.filter(comp => comp && comp.id);
        const gridLayout = componentsArray.map(comp => {
          // overrideLayout 우선 적용
          if (overrideLayout && Array.isArray(overrideLayout)) {
            const overrideItem = overrideLayout.find(item => item.i === comp.id);
            if (overrideItem) {
              return { ...overrideItem, i: comp.id, minW: 2, minH: 2 };
            }
          }

          const layoutItem = {
            i: comp.id,
            x: comp.position?.x || 0,
            y: comp.position?.y || 0,
            w: comp.position?.w || 3,
            h: comp.position?.h || 2,
            minW: 2,
            minH: 2
          };
          
          return layoutItem;
        });
        
        // overrideLayout 추가 항목 처리
        if (overrideLayout && Array.isArray(overrideLayout)) {
           overrideLayout.forEach(item => {
               if (!gridLayout.find(g => g.i === item.i)) {
                   gridLayout.push(item);
               }
           });
        }

        setLayouts({ 
          lg: gridLayout,
          md: gridLayout,
          sm: gridLayout,
          xs: gridLayout,
          xxs: gridLayout
        });
        
        layoutSyncedRef.current = true;
        setIsLayoutReady(true);
      } else {
        // 같은 템플릿의 업데이트 (저장 등) - 레이아웃 재초기화 스킵
      }
    } else if (dashboardTemplate === null) {
        setLayouts({ 
          lg: [],
          md: [],
          sm: [],
          xs: [],
          xxs: []
        });
        prevTemplateIdRef.current = null;
        layoutSyncedRef.current = false;
        setIsLayoutReady(false);
    }
  }, [dashboardTemplate, setLayouts]); // loading 의존성 제거
  
  // 자동 크기 조정 실행 여부를 추적하는 ref
  const autoResizeExecutedRef = useRef(false);
  
  // 컬럼 수 변경 감지 및 자동 크기 조정 (무한 루프 방지)
  useEffect(() => {
    if (dashboardTemplate?._autoResizeNeeded && 
        layouts.lg && 
        layouts.lg.length > 0 && 
        !autoResizeExecutedRef.current) {
      
      // 실행 플래그 설정 (무한 루프 방지)
      autoResizeExecutedRef.current = true;
      
      // 자동 크기 조정 수행 (비례 크기 조정)
      autoResizeComponents(
        'proportional_resize',
        dashboardTemplate,  // 원래 템플릿 전달 (내부에서 _resizeRatio 읽음)
        layouts,
        setLayouts,
        editMode,
        () => {
          // 완료 후 플래그 제거
          const cleanedTemplate = { ...dashboardTemplate };
          delete cleanedTemplate._autoResizeNeeded;
          delete cleanedTemplate._resizeRatio;
          setDashboardTemplateAndUpdateData(cleanedTemplate);
          
          // 편집 모드가 아닌 경우 자동으로 편집 모드 진입
          if (isAdmin && !editMode && toggleEditMode) {
            toggleEditMode();
          }
          
          // 실행 플래그 재설정
          autoResizeExecutedRef.current = false;
        },
        (error) => {
          console.error("[Dashboard] 비례 크기 조정 실패:", error);
          // 실행 플래그 재설정
          autoResizeExecutedRef.current = false;
        }
      );
    }
  }, [dashboardTemplate?._autoResizeNeeded, dashboardTemplate?._resizeRatio]);

  // 브레이크포인트 변경 시 모든 컴포넌트의 리사이즈 카운터 증가
  const handleBreakpointChangeWrapper = useCallback((newBreakpoint, newCols) => {
    // useDashboardEditMode의 handleBreakpointChange 호출
    if (handleBreakpointChange) {
      handleBreakpointChange(newBreakpoint, newCols);
    }
    
    // 모든 컴포넌트의 리사이즈 카운터 증가 (브레이크포인트 변경은 전체 레이아웃에 영향)
    const validComponents = getValidComponentsArray(dashboardTemplate);
    if (validComponents.length > 0) {
      setComponentResizeCounters(prev => {
        const newCounters = { ...prev };
        validComponents.forEach(comp => {
          newCounters[comp.id] = (newCounters[comp.id] || 0) + 1;
        });
        return newCounters;
      });
    }
  }, [dashboardTemplate, handleBreakpointChange]);
  
  // 편집모드에서 컴포넌트 클릭 핸들러 (드래그 없이 클릭만)
  const handleComponentClick = useCallback((componentId, event) => {
    if (!editMode) return;
    
    const isMultiSelect = event && (event.ctrlKey || event.metaKey || event.shiftKey);
    
    console.log('[handleComponentClick] 클릭:', {
      componentId,
      isMultiSelect
    });
    
    // Ctrl/Cmd 다중 선택
    if (isMultiSelect) {
      toggleComponentSelection(componentId, true);
      return;
    }
    
    // ⭐ 그룹 소속 확인
    const groupId = getComponentGroup(componentId);
    
    if (groupId) {
      // 그룹에 속한 경우
      const groupComponentIds = getGroupComponentIds(dashboardTemplate, groupId);
      
      // 현재 그룹 전체가 선택되어 있는지 확인
      const isGroupFullySelected = groupComponentIds.length > 0 && 
        groupComponentIds.every(id => selectedComponentIds.includes(id));
      
      if (!isGroupFullySelected) {
        // 1차 클릭: 그룹 전체 선택
        setSelectedComponentIds(groupComponentIds);
      } else {
        // 2차 클릭: 해당 컴포넌트만 선택
        setSelectedComponentIds([componentId]);
      }
    } else {
      // 그룹에 속하지 않은 경우: 기존대로 해당 컴포넌트만 선택
      setSelectedComponentIds([componentId]);
    }
  }, [editMode, toggleComponentSelection, getComponentGroup, dashboardTemplate, selectedComponentIds]);
  
  // mouseDown 핸들러는 더 이상 선택하지 않음 (드래그 처리용)
  const handleComponentSelect = useCallback((componentId, event) => {
    // 이 함수는 mouseDown용이므로 선택 처리 안함
    // 드래그 여부를 dragStart에서 판단
  }, []);
  
  // 드래그 시작 시 선택 처리를 위한 래퍼
  const handleDragStartWithSelect = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    // ⭐ 드래그 시작 플래그를 맨 처음에 설정 (toggleComponentSelection 무시용)
    window.__dragStarted = true;
    
    // 🔒 그룹 잠금 확인: 잠긴 그룹에 속한 컴포넌트는 드래그 불가
    if (oldItem && oldItem.i && isComponentInLockedGroup(dashboardTemplate, oldItem.i)) {
      window.__dragStarted = false; // 플래그 초기화
      showErrorMessage('잠긴 그룹에 속한 컴포넌트는 이동할 수 없습니다. 먼저 그룹 잠금을 해제하세요.');
      return false;
    }
    
    // 🎯 단순화된 그룹 드래그 로직
    if (oldItem && oldItem.i) {
      const isDraggedComponentSelected = isComponentSelected(oldItem.i);
      const draggedGroupId = getComponentGroup(oldItem.i);
      
      console.log('[handleDragStartWithSelect]', {
        componentId: oldItem.i,
        isSelected: isDraggedComponentSelected,
        groupId: draggedGroupId,
        currentSelection: selectedComponentIds
      });
      
      if (draggedGroupId) {
        // === 그룹에 속한 컴포넌트 ===
        if (isDraggedComponentSelected && selectedComponentIds.length === 1) {
          // 규칙 1: 이미 선택된 차트 (1개) 드래그 → 개별 이동
          window.__isDraggingGroup = false;
        } else if (!isDraggedComponentSelected) {
          // 규칙 2: 선택 안된 차트 드래그 → 그룹 전체 이동 (예약)
          const groupComponentIds = getGroupComponentIds(dashboardTemplate, draggedGroupId);
          
          // ⭐ 선택은 handleDrag에서 실제 이동 시 (깜빡임 방지)
          window.__pendingGroupSelection = groupComponentIds;
          
          window.__isDraggingGroup = true;
          window.__draggedGroupIds = groupComponentIds;
          window.__draggedMainId = oldItem.i;
        } else {
          // 규칙 3: 다중 선택 상태 유지
          window.__isDraggingGroup = selectedComponentIds.length > 1;
          if (window.__isDraggingGroup) {
            window.__draggedGroupIds = [...selectedComponentIds];
            window.__draggedMainId = oldItem.i;
          }
        }
      } else {
        // === 일반 컴포넌트 (그룹 아님) ===
        if (isDraggedComponentSelected && selectedComponentIds.length > 1) {
          // 다중 선택 드래그
          window.__isDraggingGroup = true;
          window.__draggedGroupIds = [...selectedComponentIds];
          window.__draggedMainId = oldItem.i;
        } else if (!isDraggedComponentSelected) {
          // 선택 안된 컴포넌트 드래그 → 선택 후 이동
          setSelectedComponentIds([oldItem.i]);
          window.__isDraggingGroup = false;
        } else {
          // 단일 선택 드래그
          window.__isDraggingGroup = false;
        }
      }
      
      window.__originalPositions = null;
      window.__originalTransforms = null;
    }
    
    // 원래 드래그 핸들러 호출
    if (handleDragStart) {
      handleDragStart(layout, oldItem, newItem, placeholder, e, element);
    }
  }, [handleDragStart, isComponentSelected, selectedComponentIds, dashboardTemplate, getComponentGroup, getGroupComponentIds]);
  
  // 드래그 중 그룹 이동 실시간 표시
  const handleDragWithSelect = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    // 드래그 플래그 설정
    window.__wasDragging = true;
    
    // ⭐ 예약된 그룹 선택은 나중에 적용 (깜빡임 방지)
    // handleDrag에서는 선택하지 않음
    
    // 실제 드래그가 시작되면 다중 선택 복원 (한 번만)
    if (!window.__multiSelectionRestored && window.__pendingMultiSelection && 
        window.__pendingDragComponentId === oldItem.i) {
      // 다중 선택 복원
      selectComponents(window.__pendingMultiSelection);
      
      // 그룹 드래그 설정
      window.__isDraggingGroup = true;
      window.__draggedGroupIds = [...window.__pendingMultiSelection];
      window.__draggedMainId = oldItem.i;
      window.__multiSelectionRestored = true;
      
      // 펜딩 선택 정보 삭제
      window.__pendingMultiSelection = null;
      window.__pendingDragComponentId = null;
    }
    
    // 그룹 드래그 중인지 확인
    if (window.__isDraggingGroup && window.__draggedGroupIds && window.__draggedMainId === oldItem.i) {
      // 그룹 드래그 중일 때 실시간으로 다른 선택된 컴포넌트들도 이동
      const deltaX = newItem.x - oldItem.x;
      const deltaY = newItem.y - oldItem.y;
      
      // ⭐ 원래 위치 저장 (처음 한 번만, 안전하게 초기화)
      if (!window.__originalPositions || typeof window.__originalPositions !== 'object') {
        window.__originalPositions = {};
        window.__originalPixelPositions = {};
        
        window.__draggedGroupIds.forEach(id => {
          const item = layout.find(layoutItem => layoutItem.i === id);
          if (item) {
            window.__originalPositions[id] = { x: item.x, y: item.y };
            
            // 각 컴포넌트의 실제 DOM 요소 픽셀 위치 저장
            const element = document.querySelector(`[data-component-id="${id}"]`);
            if (element) {
              const rect = element.getBoundingClientRect();
              const parentRect = element.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
              window.__originalPixelPositions[id] = {
                left: rect.left - parentRect.left,
                top: rect.top - parentRect.top
              };
            }
          }
        });
      }
      
      // 드래그 시작 시점의 원래 transform 저장 (첫 번째 호출에서만)
      if (!window.__originalTransforms) {
        window.__originalTransforms = {};
        window.__draggedGroupIds.forEach(componentId => {
          const elem = document.querySelector(`[data-component-id="${componentId}"]`);
          if (elem) {
            const style = window.getComputedStyle(elem);
            const currentTransform = style.transform;
            let currentX = 0, currentY = 0;
            
            if (currentTransform && currentTransform !== 'none') {
              const matrix = currentTransform.match(/matrix\(([^)]+)\)/);
              if (matrix) {
                const values = matrix[1].split(', ');
                currentX = parseFloat(values[4]) || 0;
                currentY = parseFloat(values[5]) || 0;
              }
            }
            
            window.__originalTransforms[componentId] = { x: currentX, y: currentY };
          }
        });
      }
      
      // 드래그 중인 요소의 현재 픽셀 이동량 계산
      const draggedElement = element || document.querySelector(`[data-component-id="${oldItem.i}"]`);
      let translateX = 0, translateY = 0;  // 변수를 if 블록 밖으로 이동
      
      if (draggedElement) {
        const transform = window.getComputedStyle(draggedElement).transform;
        
        if (transform && transform !== 'none') {
          const matrix = transform.match(/matrix\(([^)]+)\)/);
          if (matrix) {
            const values = matrix[1].split(', ');
            translateX = parseFloat(values[4]) || 0;
            translateY = parseFloat(values[5]) || 0;
          }
        }
        
        // 드래그 시작점으로부터의 이동 거리 계산
        const dragStartTransform = window.__originalTransforms[oldItem.i] || { x: 0, y: 0 };
        const movementX = translateX - dragStartTransform.x;
        const movementY = translateY - dragStartTransform.y;
        
        // 의미있는 이동이 있을 때만 transform 적용 (클릭과 드래그 구분)
        const MIN_MOVEMENT = 2; // 픽셀 단위 최소 이동 거리
        const hasMeaningfulMovement = Math.abs(movementX) > MIN_MOVEMENT || Math.abs(movementY) > MIN_MOVEMENT;
        
        if (hasMeaningfulMovement) {
          // 다른 선택된 컴포넌트들에 상대적 이동 적용 (실시간 시각적 이동)
          window.__draggedGroupIds.forEach(componentId => {
            if (componentId !== oldItem.i) {  // 드래그 중인 컴포넌트는 제외
              const otherElement = document.querySelector(`[data-component-id="${componentId}"]`);
              if (otherElement && window.__originalTransforms[componentId]) {
                const originalTransform = window.__originalTransforms[componentId];
                // 각 요소의 원래 위치에서 동일한 이동량만큼 이동
                const newX = originalTransform.x + movementX;
                const newY = originalTransform.y + movementY;
                
                otherElement.style.transform = `translate(${newX}px, ${newY}px)`;
                otherElement.style.transition = 'none';
                otherElement.style.zIndex = '1000';
                otherElement.style.pointerEvents = 'none';
              }
            }
          });
          
          // ⭐ 그룹 바운딩 박스도 함께 이동
          const draggedGroupId = getComponentGroup(oldItem.i);
          if (draggedGroupId) {
            const groupBoxElement = document.querySelector(`[data-group-id="${draggedGroupId}"]`);
            if (groupBoxElement) {
              groupBoxElement.style.transform = `translate(${movementX}px, ${movementY}px)`;
              groupBoxElement.style.transition = 'none';
            }
          }
          
          // 실제 드래그가 일어났음을 표시
          window.__actualDragOccurred = true;
        }
      }
      
      // ⭐⭐ 그리드 단위 이동이 있을 때만 레이아웃 업데이트 (deltaX, deltaY가 0이 아닐 때만)
      // 이렇게 하면 작은 드래그나 클릭 시 레이아웃이 잘못 업데이트되는 것을 방지
      if (deltaX !== 0 || deltaY !== 0) {
        console.log('[handleDrag] 그리드 단위 이동 감지, modifiedLayout 생성:', { deltaX, deltaY });
        
        // 다른 선택된 컴포넌트들의 위치를 업데이트한 레이아웃 반환 (최종 위치 저장용)
        const modifiedLayout = layout.map(item => {
          // 드래그 중인 메인 아이템은 그대로 둠
          if (item.i === oldItem.i) {
            return item;
          }
          
          // 선택된 다른 컴포넌트들은 같은 delta만큼 이동
          if (window.__draggedGroupIds.includes(item.i)) {
            const originalPos = window.__originalPositions?.[item.i];
            if (originalPos && typeof originalPos.x === 'number' && typeof originalPos.y === 'number') {
              return {
                ...item,
                x: originalPos.x + deltaX,
                y: originalPos.y + deltaY,
                moved: true,
                static: false
              };
            } else {
              console.warn('[handleDrag] originalPos 없음, 현재 위치 유지:', item.i);
              return item;
            }
          }
          
          // 나머지 컴포넌트는 그대로
          return item;
        });
        
        // 드래그 종료 시 사용할 레이아웃 저장
        window.__pendingGroupLayout = modifiedLayout;
        
        // 원래 드래그 핸들러 호출 - 수정된 레이아웃 전달
        if (handleDrag) {
          handleDrag(modifiedLayout, oldItem, newItem, placeholder, e, element);
        }
      } else {
        console.log('[handleDrag] 그리드 단위 이동 없음 (deltaX=0, deltaY=0), handleDrag 호출 생략');
        
        // ⭐⭐ deltaX=0, deltaY=0이면 handleDrag를 호출하지 않음
        // React Grid Layout이 내부적으로 잘못된 레이아웃을 계산하는 것을 방지
        // handleDrag를 호출하지 않으면 레이아웃 상태가 변경되지 않음
      }
      
      return;
    }
    
    // 그룹 드래그가 아닌 경우 원래 핸들러 호출
    if (handleDrag) {
      handleDrag(layout, oldItem, newItem, placeholder, e, element);
    }
  }, [handleDrag, selectComponents]);

  // 드래그 종료 시 선택 유지 및 그룹 이동 처리
  const handleDragStopWithSelect = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    const hasMoved = oldItem.x !== newItem.x || oldItem.y !== newItem.y;
    
    console.log('[handleDragStopWithSelect]', {
      oldPos: { x: oldItem.x, y: oldItem.y },
      newPos: { x: newItem.x, y: newItem.y },
      hasMoved,
      isDraggingGroup: window.__isDraggingGroup
    });
    
    // ⭐ 이동하지 않았으면 클릭으로 처리
    if (!hasMoved && oldItem && oldItem.i) {
      console.log('[handleDragStopWithSelect] 클릭 감지 → 개별 선택 + 전체 초기화:', oldItem.i);
      
      // ⭐⭐ 그룹 드래그 중이었다면 transform만 제거 (레이아웃은 이미 올바름)
      const wasGroupDragging = window.__isDraggingGroup;
      const draggedGroupId = oldItem?.i ? getComponentGroup(oldItem.i) : null;
      
      if (wasGroupDragging) {
        // ⭐⭐ CSS transform을 드래그 시작 전 상태로 복원
        if (window.__draggedGroupIds && window.__originalTransforms) {
          const draggedIds = window.__draggedGroupIds;
          const originalTransforms = window.__originalTransforms;
          
          draggedIds.forEach(componentId => {
            const element = document.querySelector(`[data-component-id="${componentId}"]`);
            if (element) {
              const beforeTransform = element.style.transform;
              const originalTransform = originalTransforms[componentId];
              
              // 원래 transform 상태로 복원 (대부분 {x: 0, y: 0})
              if (originalTransform && (originalTransform.x !== 0 || originalTransform.y !== 0)) {
                element.style.transform = `translate(${originalTransform.x}px, ${originalTransform.y}px)`;
                console.log(`[handleDragStop] ${componentId} transform: "${beforeTransform}" → "translate(${originalTransform.x}px, ${originalTransform.y}px)"`);
              } else {
                element.style.transform = '';
                console.log(`[handleDragStop] ${componentId} transform: "${beforeTransform}" → ""`);
              }
              
              element.style.transition = '';
              element.style.zIndex = '';
              element.style.pointerEvents = '';
            } else {
              console.warn(`[handleDragStop] 엘리먼트 없음: ${componentId}`);
            }
          });
          
          // ⭐ 그룹 박스 transform도 제거
          if (draggedGroupId) {
            const groupBoxElement = document.querySelector(`[data-group-id="${draggedGroupId}"]`);
            if (groupBoxElement) {
              groupBoxElement.style.transform = '';
              groupBoxElement.style.transition = '';
            }
          }
        }
        
        // 모든 그룹 관련 플래그 즉시 초기화
        window.__isDraggingGroup = false;
        window.__draggedGroupIds = null;
        window.__draggedMainId = null;
        window.__originalPositions = null;
        window.__originalPixelPositions = null;
        window.__originalTransforms = null;
        window.__actualDragOccurred = false;
        window.__pendingGroupLayout = null;
        window.__pendingGroupSelection = null;
        window.__groupSelectionApplied = false;
      }
      
      // ⭐ 2단계 그룹 선택 로직
      const clickedGroupId = getComponentGroup(oldItem.i);
      
      if (clickedGroupId) {
        // 그룹에 속한 경우
        const groupComponentIds = getGroupComponentIds(dashboardTemplate, clickedGroupId);
        
        // 현재 그룹 전체가 선택되어 있는지 확인
        const isGroupFullySelected = groupComponentIds.length > 0 && 
          groupComponentIds.every(id => selectedComponentIds.includes(id));
        
        if (!isGroupFullySelected) {
          // 1차 클릭: 그룹 전체 선택
          setSelectedComponentIds(groupComponentIds);
        } else {
          // 2차 클릭: 해당 컴포넌트만 선택
          setSelectedComponentIds([oldItem.i]);
        }
      } else {
        // 그룹에 속하지 않은 경우: 해당 컴포넌트만 선택
        setSelectedComponentIds([oldItem.i]);
      }
      
      // ⭐ 클릭으로 감지되었을 때는 원본 handleDragStop을 호출하지 않음
      // deltaX=0, deltaY=0이므로 레이아웃은 이미 올바른 상태이고, transform만 제거하면 됨
      console.log('[handleDragStop] 클릭 감지 → 원본 핸들러 호출 생략 (레이아웃 변경 없음)');
      
      return; // 아래 로직 실행 방지
    }
    
    // ⭐ 실제 이동이 있었으면 예약된 그룹 선택 적용
    if (hasMoved && window.__pendingGroupSelection) {
      setSelectedComponentIds(window.__pendingGroupSelection);
    }
    
    // 드래그 종료 시 모든 플래그와 임시 데이터 초기화
    setTimeout(() => {
      window.__dragStarted = false;
      window.__wasDragging = false;
    }, 100);
    
    window.__previousMultiSelection = null;
    window.__pendingMultiSelection = null;
    window.__pendingDragComponentId = null;
    window.__pendingGroupSelection = null;
    window.__groupSelectionApplied = false;
    
    // finalLayout을 함수 스코프에서 선언
    let finalLayout = layout;
    
    if (window.__isDraggingGroup && window.__draggedGroupIds && window.__draggedMainId === oldItem.i) {
      // 그룹 드래그가 끝났을 때
      const deltaX = newItem.x - oldItem.x;
      const deltaY = newItem.y - oldItem.y;
      
      // CSS transform 제거 (시각적 이동 효과 제거) - 실제 드래그가 있었을 때만
      if (window.__actualDragOccurred) {
        window.__draggedGroupIds.forEach(componentId => {
          if (componentId !== oldItem.i) {
            const element = document.querySelector(`[data-component-id="${componentId}"]`);
            if (element) {
              element.style.transform = '';
              element.style.transition = '';
              element.style.zIndex = '';
              element.style.pointerEvents = '';
            }
          }
        });
        
        // ⭐ 그룹 박스 transform도 제거
        const groupId = getComponentGroup(oldItem.i);
        if (groupId) {
          const groupBoxElement = document.querySelector(`[data-group-id="${groupId}"]`);
          if (groupBoxElement) {
            groupBoxElement.style.transform = '';
            groupBoxElement.style.transition = '';
          }
        }
      }
      
      // 실제 드래그가 일어났을 때만 레이아웃 업데이트
      if (window.__actualDragOccurred && (deltaX !== 0 || deltaY !== 0)) {
        // 최종 레이아웃 생성 (펜딩 레이아웃이 없는 경우를 대비)
        if (window.__pendingGroupLayout) {
          finalLayout = window.__pendingGroupLayout;
        } else {
          // 펜딩 레이아웃이 없으면 여기서 계산
          finalLayout = layout.map(item => {
            if (item.i !== oldItem.i && window.__draggedGroupIds.includes(item.i)) {
              const originalPos = window.__originalPositions?.[item.i];
              if (originalPos && typeof originalPos.x === 'number' && typeof originalPos.y === 'number') {
                return {
                  ...item,
                  x: originalPos.x + deltaX,
                  y: originalPos.y + deltaY
                };
              } else {
                console.warn('[handleDragStop] originalPos 없음, 현재 위치 유지:', item.i);
                return item;
              }
            }
            return item;
          });
        }
        
        // 모든 브레이크포인트에 적용
        setLayouts({
          lg: finalLayout,
          md: finalLayout,
          sm: finalLayout,
          xs: finalLayout,
          xxs: finalLayout
        });
        
        // 템플릿과 동기화
        syncLayoutAndTemplate({ lg: finalLayout }, 'group_drag');
        setHasLayoutChanged(true);
      }
      
      // 플래그 초기화
      window.__isDraggingGroup = false;
      window.__draggedGroupIds = null;
      window.__draggedMainId = null;
      window.__originalPositions = null;
      window.__originalPixelPositions = null;
      window.__originalTransforms = null;
      window.__actualDragOccurred = false;
      window.__pendingGroupLayout = null;
      window.__previousMultiSelection = null;
      
      // 원래 드래그 핸들러 호출 - 수정된 레이아웃 전달
      if (handleDragStop) {
        handleDragStop(finalLayout, oldItem, newItem, placeholder, e, element);
      }
      
      return; // 여기서 리턴하여 아래의 기본 handleDragStop 호출 방지
    }
    
    // 그룹 드래그가 아닌 경우 원래 핸들러 호출
    if (handleDragStop) {
      handleDragStop(layout, oldItem, newItem, placeholder, e, element);
    }
    
    // ⭐ 항상 초기화 (그룹 드래그가 아니어도)
    window.__isDraggingGroup = false;
    window.__draggedGroupIds = null;
    window.__draggedMainId = null;
    window.__originalPositions = null;
    window.__originalPixelPositions = null;
    window.__originalTransforms = null;
    window.__actualDragOccurred = false;
    window.__pendingGroupLayout = null;
  }, [handleDragStop, setLayouts, syncLayoutAndTemplate, setHasLayoutChanged]);
  
  // 리사이즈 시작 시 잠금 확인 래퍼
  const handleResizeStartWithLockCheck = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!oldItem || !oldItem.i) return;
    
    // 🔒 그룹 잠금 확인: 잠긴 그룹에 속한 컴포넌트는 리사이즈 불가
    if (isComponentInLockedGroup(dashboardTemplate, oldItem.i)) {
      showErrorMessage('잠긴 그룹에 속한 컴포넌트는 크기를 변경할 수 없습니다. 먼저 그룹 잠금을 해제하세요.');
      return false;
    }
    
    // 원래 핸들러 호출
    if (handleResizeStart) {
      return handleResizeStart(layout, oldItem, newItem, placeholder, e, element);
    }
  }, [handleResizeStart, dashboardTemplate]);
  
  // 차트 클릭 이벤트 핸들러
  
  const handleChartClick = useCallback((component, clickedData, eventType) => {
    
    // 컴포넌트 ID와 이벤트 타입에 따라 특별한 처리
    let categoryValue = clickedData.name || clickedData.value;
    
    // 클릭 파라미터 설정
    const newParams = {
      clicked_category: categoryValue,
      clicked_value: clickedData.value,
      clicked_series: clickedData.seriesName,
      clicked_index: clickedData.dataIndex
    };
    
    // self 이벤트 처리
    if (eventType === 'self') {
      // 리셋 버튼 클릭 시 원래 상태로 복원
      if (clickedData.type === 'reset') {
        // 해당 컴포넌트의 활성 쿼리 제거
        const newActiveQueries = { ...activeQueriesRef.current };
        delete newActiveQueries[component.id];
        flushSync(() => {
          setActiveQueries(newActiveQueries);
        });
        
        // 클릭된 차트 ID도 초기화
        if (clickedChartIds.length > 0) {
          setClickedChartIds([]);
        }
        
        // 명시적으로 null을 전달하여 원본 쿼리로 복원
        refreshChartData(component.id, null);
        return;
      }
      
      // on_clicked 템플릿이 있는 경우 처리 (현재는 사용하지 않음)
    }
    
    // 현재 차트가 on_click 퍼블리셔인지 확인
    const hasOnClick = !!(component.on_click);
    
    // 다른 차트들의 on_clicked_other 이벤트 처리
    const newActiveQueries = { ...activeQueriesRef.current };
    let hasUpdates = false;
    
    dashboardTemplate.components.forEach(targetComponent => {
      // on_clicked_other 또는 on_clicked_others 체크 (둘 다 지원)
      const onClickedOther = targetComponent.on_clicked_other || targetComponent.on_clicked_others;
      
      // 디버그: 각 컴포넌트의 on_clicked_other(s) 확인
      if (targetComponent.id === 'hanioniq5_caution_alarms') {
      }
      
      if (targetComponent.id !== component.id && onClickedOther && onClickedOther[component.id]) {
        
        // 대상 차트의 on_clicked_other(s) 템플릿 가져오기
        const eventTemplate = onClickedOther[component.id];
        
        // 활성 쿼리 업데이트 (템플릿에서 명시적으로 설정된 alertConditions만 사용)
        newActiveQueries[targetComponent.id] = {
          query: eventTemplate.data_source.query,
          title: eventTemplate.title,
          clickParams: newParams,
          sourceComponent: component.id,
          // 템플릿에서 명시적으로 설정된 alertConditions만 사용 (자동 동기화 제거)
          alertConditions: eventTemplate.alertConditions || null
        };
        hasUpdates = true;
      }
    });
    
    // 독립선택 그룹 및 개별 차트 독립선택을 고려한 차트 선택 처리 함수
    const updateClickedCharts = (clickedComponentId) => {
      const clickedComponent = dashboardTemplate.components.find(c => c.id === clickedComponentId);
      const clickedGroupId = getComponentGroup(clickedComponentId);
      const clickedGroup = clickedGroupId ? dashboardTemplate?.groups?.find(g => g.id === clickedGroupId) : null;
      
      // 개별 차트 또는 그룹의 독립선택 여부 확인
      const isClickedGroupIsolated = clickedGroup?.isolatedSelection === true;
      const isClickedChartIsolated = clickedComponent?.isolatedSelection === true;
      const isClickedIsolated = isClickedGroupIsolated || isClickedChartIsolated;
      
      // 이미 선택되어 있는지 확인
      const isAlreadySelected = clickedChartIds.includes(clickedComponentId);
      
      // 독립선택 차트를 재클릭하면 토글 (선택 해제)
      if (isAlreadySelected && isClickedIsolated) {
        const newSelection = clickedChartIds.filter(id => id !== clickedComponentId);
        setClickedChartIds(newSelection);
        
        // 선택 해제 시 clearVariablesOnDeselect 옵션 확인
        if (clickedComponent?.on_click?.clearVariablesOnDeselect && removeVariablesBySource) {
          removeVariablesBySource(clickedComponentId);
        }
        
        return;
      }
      
      // 새로운 선택 배열 계산
      const newSelection = clickedChartIds.filter(id => {
        const idComponent = dashboardTemplate.components.find(c => c.id === id);
        const idGroupId = getComponentGroup(id);
        const idGroup = idGroupId ? dashboardTemplate?.groups?.find(g => g.id === idGroupId) : null;
        
        // 개별 차트 독립선택 확인
        const isIdChartIsolated = idComponent?.isolatedSelection === true;
        const isIdGroupIsolated = idGroup?.isolatedSelection === true;
        
        // 독립선택 그룹 내 차트는 유지 (단, 클릭된 차트와 같은 그룹이면 제외)
        if (isIdGroupIsolated) {
          return idGroupId !== clickedGroupId; // 다른 독립선택 그룹만 유지
        }
        
        // 개별 차트 독립선택이면 유지
        if (isIdChartIsolated) {
          return true;
        }
        
        // 클릭된 차트가 독립선택이면, 일반 차트도 유지
        if (isClickedIsolated) {
          return true; // 일반 차트 유지
        }
        
        // 클릭된 차트가 일반 차트면, 다른 일반 차트는 제거
        return false;
      });
      
      // 클릭된 차트 추가
      newSelection.push(clickedComponentId);
      
      // 선택 해제된 차트들 확인 및 변수 삭제
      const deselectedChartIds = clickedChartIds.filter(id => !newSelection.includes(id));
      if (deselectedChartIds.length > 0 && removeVariablesBySource) {
        deselectedChartIds.forEach(deselectedId => {
          const deselectedComponent = dashboardTemplate.components.find(c => c.id === deselectedId);
          if (deselectedComponent?.on_click?.clearVariablesOnDeselect) {
            removeVariablesBySource(deselectedId);
          }
        });
      }
      
      setClickedChartIds(newSelection);
    };
    
    // on_click 퍼블리셔가 있으면 선택 상태 유지, 없으면 기존 로직 따름
    if (hasOnClick) {
      // on_click 퍼블리셔가 있는 차트는 독립선택 로직 적용
      updateClickedCharts(component.id);
      
      // 활성 쿼리 업데이트 (있는 경우에만)
      if (hasUpdates) {
        flushSync(() => {
          setActiveQueries(newActiveQueries);
        });
      } else {
        // on_click은 있지만 구독자가 없는 경우, 기존 활성 쿼리 초기화
        if (Object.keys(activeQueries).length > 0) {
          flushSync(() => {
            setActiveQueries({});
          });
        }
      }
    } else if (!hasUpdates) {
      // on_click 퍼블리셔가 없고 업데이트도 없는 경우
      // 이전에 클릭된 차트가 있었는지 확인
      const hadPreviousClick = clickedChartIds.length > 0 || Object.keys(activeQueriesRef.current).length > 0;
      
      if (hadPreviousClick) {
        // 선택 해제되는 차트들의 변수 삭제
        if (clickedChartIds.length > 0 && removeVariablesBySource) {
          clickedChartIds.forEach(deselectedId => {
            const deselectedComponent = dashboardTemplate.components.find(c => c.id === deselectedId);
            if (deselectedComponent?.on_click?.clearVariablesOnDeselect) {
              removeVariablesBySource(deselectedId);
            }
          });
        }
        
        setClickedChartIds([]);
        
        // 기존 활성 쿼리가 있었다면 모두 초기화
        if (Object.keys(activeQueries).length > 0) {
          flushSync(() => {
            setActiveQueries({});
          });
        }
      }
    } else {
      // on_click은 없지만 다른 차트에 영향을 주는 경우
      updateClickedCharts(component.id);
      flushSync(() => {
        setActiveQueries(newActiveQueries);
      });
    }
  }, [dashboardTemplate, updateSpecificComponents, clickedChartIds, refreshChartData, getComponentGroup]);
  

  // 에러 발생 시 부모에 알림
  useEffect(() => {
    if (error && onError) {
      onError({
        message: error.message || error.toString(),
        status: error.response?.status || error.status || 'unknown'
      });
    }
  }, [error, onError, isPopup, dashboardId]);

  // 로드 완료 시 부모에 알림 (대시보드 ID별 1회만)
  const onLoadFiredRef = useRef({});
  useEffect(() => {
    // 템플릿만 로드되면 일단 onLoad 호출 (데이터는 백그라운드에서 로드)
    const currentId = dashboardTemplate?.dashboard_id || dashboardTemplate?.dashboardId;
    if (!templateLoading && !error && dashboardTemplate && onLoad) {
      if (!currentId) {
        // ID가 없으면 기본 동작 (과거 호환)
        onLoad();
      } else if (!onLoadFiredRef.current[currentId]) {
        onLoadFiredRef.current[currentId] = true;
        onLoad();
      }
    }
    // 인쇄 모드: 모든 차트가 ready 신호를 보낸 이후에만 인쇄 이벤트 디스패치
    // 템플릿 준비 후 즉시 리스닝을 시작하되, 실제 디스패치는 모든 컴포넌트 렌더/준비를 만족해야 함
    if (printMode && !templateLoading && !error && dashboardTemplate) {
      try {
        const container = document.querySelector('.dashboard-container');
        if (!container) return;

        let cleanedUp = false;
        let timeoutId = null;
        let observer = null;

        const cleanup = () => {
          if (cleanedUp) return;
          cleanedUp = true;
          try { container.removeEventListener('chart-ready', onSignal); } catch (_) {}
          if (observer) { try { observer.disconnect(); } catch (_) {} observer = null; }
          if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
        };

        const dispatchIfReady = () => {
          // 실제 렌더된 동적 차트 DOM 수를 기준으로 집계 (템플릿 개수와 불일치 방지)
          const nodes = container.querySelectorAll('.dynamic-chart-container');
          const expectedTotal = nodes.length;
          if (expectedTotal === 0) return false; // 아직 마운트되지 않음
          const ready = container.querySelectorAll('.dynamic-chart-container[data-echarts-ready="true"]').length;
          if (ready === expectedTotal) {
            cleanup();
            window.dispatchEvent(new Event('dashboard-ready-for-print'));
            return true;
          }
          return false;
        };

        const onSignal = () => {
          dispatchIfReady();
        };

        // 즉시 한 번 검사. 아직 부족하면 DOM 변경을 감시하여 차트가 마운트/업데이트되면 재검사
        if (!dispatchIfReady()) {
          container.addEventListener('chart-ready', onSignal);
          observer = new MutationObserver(() => { dispatchIfReady(); });
          observer.observe(container, { childList: true, subtree: true });
          // 안전장치: 3초 이후에는 강제 진행 (환경에 따라 조정 가능)
          timeoutId = setTimeout(() => {
            cleanup();
            window.dispatchEvent(new Event('dashboard-ready-for-print'));
          }, 3000);
        }
      } catch (e) {
        console.warn('[Dashboard] print ready event dispatch failed:', e);
      }
    }
  }, [templateLoading, dataLoading, error, dashboardTemplate, onLoad, printMode]);

  // Filter out components that do not have a valid 'id' before passing to ResponsiveGridLayout
  const validComponentsArray = getValidComponentsArray(dashboardTemplate);

  // 템플릿 로딩 중에만 전체 스켈레톤 표시
  if (templateLoading) {
    return <DashboardSkeleton dashboardType="simple" />;
  }
  
  // 템플릿 로딩 완료 후에는 실제 DashboardGrid 표시
  // 개별 컴포넌트들은 componentData 유무에 따라 스켈레톤/실제 차트 표시

  // 에러 표시 (팝업 모드에서는 부모가 처리)
  if (error && !isPopup) {
    const authError = isAuthError(error);
    
    return (
      <div className="dashboard-error">
        <h3>대시보드 로드 오류</h3>
        {authError ? (
          <>
            <p>인증 세션이 만료되었습니다. 로그인 페이지로 이동합니다.</p>
            <button 
              onClick={() => {
                console.warn('[Dashboard] 인증 에러로 인한 수동 로그인 리다이렉트');
                
                // 쿠키 정리 (dashboard_id는 백엔드에서 관리)
                document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                document.cookie = 'current_tenant=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                
                // 로컬 스토리지 정리
                localStorage.removeItem('token');
                
                // 현재 경로 저장
                sessionStorage.setItem('redirect_after_login', window.location.pathname);
                
                // 로그인 페이지로 이동
                window.location.href = '/login';
              }}
              className="auth-retry-button"
            >
              로그인 페이지로 이동
            </button>
          </>
        ) : (
          <>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>다시 시도</button>
          </>
        )}
      </div>
    );
  }

  // 대시보드 템플릿이 없는 경우
  if (!dashboardTemplate) {
    return (
      <div className="dashboard-empty">
        <h3>대시보드 템플릿이 없습니다</h3>
        <p>대시보드를 생성하거나 관리자에게 문의하세요.</p>
      </div>
    );
  }

  // 배경 렌더링에 사용할 효과적인 값 계산 (템플릿 옵션 우선, 그 다음 서버 값, 그리고 pending 우선 적용)
  const effectiveBackground = (() => {
    const options = dashboardTemplate?.dashboard_options || {};
    // 템플릿(dashboard_options) 우선 → 서버 보관 필드 보조
    const base = {
      url: options.background_image_url ?? dashboardTemplate?.background_image_url ?? null,
      opacity: options.background_opacity ?? dashboardTemplate?.background_opacity ?? 1,
      position: options.background_position ?? dashboardTemplate?.background_position ?? 'center',
      size: options.background_size ?? dashboardTemplate?.background_size ?? 'cover'
    };
    if (!pendingBackground) return base;
    if (pendingBackground.removeFlag) {
      return { url: null, opacity: 1, position: 'center', size: 'cover' };
    }
    return {
      url: pendingBackground.previewUrl || base.url,
      opacity: pendingBackground.background_opacity ?? base.opacity,
      position: pendingBackground.background_position || base.position,
      size: pendingBackground.background_size || base.size
    };
  })();

  // 런타임 모드에서 대시보드 목록 로딩 중인 경우 처리 (인쇄 모드는 우회)
  if (!editMode && isDashboardListLoading && !printMode) {
    return (
      <div className="dashboard-wrapper" ref={wrapperRef}>
        <div className="empty-dashboard-message" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#666',
          fontSize: '18px',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div style={{ marginBottom: '10px' }}>대시보드 목록을 불러오는 중...</div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            잠시만 기다려 주세요.
          </div>
        </div>
      </div>
    );
  }

  // 런타임 모드에서 대시보드 목록이 비어있는 경우 처리 (인쇄 모드는 우회)
  if (!editMode && !isDashboardListLoading && dashboardList && dashboardList.length === 0 && !printMode) {
    return (
      <div className="dashboard-wrapper" ref={wrapperRef}>
        <div className="empty-dashboard-message" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#666',
          fontSize: '18px',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
          <div style={{ marginBottom: '10px' }}>표시할 대시보드가 없습니다</div>
          <div style={{ fontSize: '14px', color: '#999', marginBottom: '30px' }}>
            모든 대시보드가 숨겨져 있거나 사용 가능한 대시보드가 없습니다.
          </div>
          {isAdmin && (
            <button
              onClick={toggleEditMode}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#40a9ff'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1890ff'}
            >
              ✎ 편집 모드로 들어가기
            </button>
          )}
        </div>
        
        {/* 플로팅 편집 버튼도 표시 (일관성을 위해) - 관리자에게만 표시, 팝업 모드에서는 숨김 */}
        {isAdmin && !isPopup && (
          <div className="floating-edit-control-wrapper">
            <div className="floating-edit-control">
              <button
                className="floating-edit-btn"
                onClick={toggleEditMode}
                title="편집 모드 시작"
              >
                ✎
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper" ref={wrapperRef}>
        {/* 요청 기반 오버레이 - in-flight 요청이 있을 때만 표시 */}
        {requestOverlayActive && (
          <>
            <style>
              {`
                @keyframes fadeInGlobal {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.6; }
                }
                
                .global-update-overlay {
                  position: fixed;
                  inset: 0;
                  background: rgba(255, 255, 255, 0.5);
                  backdrop-filter: blur(2px);
                  z-index: 9998;
                  animation: fadeInGlobal 0.3s ease-in;
                  pointer-events: none;
                }
                
                .global-update-indicator {
                  position: fixed;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  background: rgba(0, 123, 255, 0.9);
                  color: white;
                  padding: 12px 24px;
                  border-radius: 24px;
                  font-size: 14px;
                  font-weight: 500;
                  z-index: 9999;
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
                  animation: pulse 1.5s ease-in-out infinite;
                }
                
                .global-update-dots {
                  display: flex;
                  gap: 4px;
                }
                
                .global-update-dot {
                  width: 6px;
                  height: 6px;
                  background: white;
                  border-radius: 50%;
                  animation: pulse 1.5s ease-in-out infinite;
                }
                
                .global-update-dot:nth-child(2) {
                  animation-delay: 0.2s;
                }
                
                .global-update-dot:nth-child(3) {
                  animation-delay: 0.4s;
                }
              `}
            </style>
            <div className="global-update-overlay" />
            <div className="global-update-indicator">
              <span>대시보드 업데이트 중</span>
              <div className="global-update-dots">
                <div className="global-update-dot" />
                <div className="global-update-dot" />
                <div className="global-update-dot" />
              </div>
            </div>
          </>
        )}
        
        {/* 편집 모드일 때만 툴바 표시 - 스크롤 컨테이너 밖으로 이동 */}
        {editMode && (
          <div className="dashboard-edit-toolbar" ref={toolbarRef}>
            <DashboardToolbar
              editMode={editMode}
              isSavingLayout={isSavingLayout}
              dashboardTemplate={dashboardTemplate}
              layouts={layouts}
              setLayouts={setLayouts}
              compactType={compactType}
              saveAndExit={handleSaveAndExitWithBackground}
              cancelChanges={handleCancelChanges}
              exitWithoutSaving={handleExitWithoutSaving}
              openOptionsModal={openOptionsModal}
              openBackgroundModal={openBackgroundModal}
              openAlarmManagement={() => setShowAlarmManagement(true)}
              addNewChart={addNewChart}
              toggleCompactType={toggleCompactType}
              canUndo={canUndo}
              canRedo={canRedo}
              undo={undo}
              redo={redo}
              undoStack={undoStack}
              redoStack={redoStack}
              gridColumns={gridColumns}
              onGridColumnsChange={handleGridColumnsChange}
            />
          </div>
        )}

        <div 
          ref={dashboardContainerRef}
          className={`dashboard-container ${editMode ? 'edit-mode-active' : ''} ${isDragging ? 'dragging' : ''} ${isSelecting ? 'selecting' : ''} ${transparentMode ? 'transparent-mode' : ''}`}
          onClick={(e) => {
            // 빈 공간 클릭 시 선택 해제 (드래그 선택 중이거나 직후가 아닐 때만)
            if (editMode && !isSelecting && !justFinishedSelecting && 
                (e.target === e.currentTarget || 
                e.target.classList.contains('dashboard-grid-layout') ||
                e.target.classList.contains('grid-dots-overlay') ||
                e.target.classList.contains('react-grid-layout'))) {
              clearSelection();
            }
          }}
          style={{
            // 투명 모드일 때는 배경을 투명하게, 아니면 기본 스타일 적용
            ...(transparentMode ? { backgroundColor: 'transparent', padding: '0' } : calculateDashboardStyle(dashboardTemplate)),
            ...(editMode ? { paddingTop: `${toolbarHeight}px` } : {}),
            ...(effectiveBackground.url && !transparentMode ? {
              backgroundImage: `url(${effectiveBackground.url})`,
              backgroundSize: effectiveBackground.size || 'cover',
              backgroundPosition: effectiveBackground.position || 'center',
              backgroundRepeat: 'no-repeat',
              position: 'relative'
            } : {})
          }}
        >
        {/* 배경 이미지 오버레이 (투명도 조절용) */}
        {effectiveBackground.url && (
          <div 
            className="dashboard-background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, ' + (1 - (effectiveBackground.opacity || 1)) + ')',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
        )}
        
        {/* 대시보드 그리드 - 레이아웃이 준비되고 전환 중이 아닐 때만 렌더링 */}
        {dashboardTemplate && isLayoutReady && !isGridTransitioning ? (
          <DashboardGrid
            dashboardTemplate={{...dashboardTemplate, layout: {...dashboardTemplate.layout, columns: gridColumns}}}
            layouts={layouts}
            editMode={editMode}
            overrideRowHeight={overrideRowHeight} // rowHeight 오버라이드 전달
          validComponentsArray={validComponentsArray}
          chartsData={chartsData}
          componentResizeCounters={componentResizeCounters}
          compactType={compactType}
          getEffectiveCompactType={getEffectiveCompactType}
          handleLayoutChange={handleLayoutChange}
          handleBreakpointChange={handleBreakpointChangeWrapper}
          handleDragStart={handleDragStartWithSelect}
          handleDrag={handleDragWithSelect}
          handleDragStop={handleDragStopWithSelect}
          handleResizeStart={handleResizeStartWithLockCheck}
          handleResize={handleResize}
          handleResizeStop={handleResizeStop}
          handleComponentDoubleClick={handleComponentDoubleClick}
          deleteComponent={deleteComponent}
          refreshChartData={refreshChartData}
          refreshAllChartsData={refreshAllChartsData}
          triggerComponentResize={triggerComponentResize}
          isUndoRedoOperation={isUndoRedoOperation}
          isChatbotAddingComponent={isChatbotAddingComponent}
          textLabelPreviewData={null}
          setIsDragging={setIsDragging}
          onChartClick={handleChartClick}
          activeChartState={activeQueries}
          clickedChartIds={clickedChartIds}
          selectedComponentIds={selectedComponentIds}
          isComponentSelected={isComponentSelected}
          onComponentSelect={handleComponentSelect}
          onComponentClick={handleComponentClick}
          onCreateGroup={handleCreateGroup}
          onUngroupComponents={handleUngroupComponents}
          onToggleGroupLock={toggleGroupLock}
          onToggleGroupIsolation={toggleGroupIsolation}
          onToggleChartIsolation={toggleChartIsolation}
          onSelectGroupComponents={selectGroupComponents}
          getComponentGroup={getComponentGroup}
          onGroupResize={handleGroupResize}
          onGroupDrag={handleGroupDrag}
          printMode={printMode}
          currentPopupId={currentPopupId}
        />
        ) : (
          // layouts가 초기화되지 않았을 때 스켈레톤 표시
          <DashboardSkeleton dashboardType="simple" />
        )}
        
        {/* 드래그 선택 박스 - 대시보드 컨테이너 내부에 위치 */}
        {editMode && (
          <SelectionBox 
            isSelecting={isSelecting}
            startPoint={selectionStart}
            endPoint={selectionEnd}
          />
        )}
        
        </div>
        
        {/* 대시보드 옵션 편집 모달 */}
        <DashboardOptionsEditor 
          open={showOptionsModal}
          onClose={closeOptionsModal}
          dashboard={dashboardTemplate}
          dashboardId={dashboardTemplate?.dashboard_id || dashboardTemplate?.dashboardId}
          onSave={handleDashboardOptionsSave}
          onPendingBackgroundChange={(pending) => {
            setPendingBackground(pending);
          }}
        />

        {/* 자연어 컴포넌트 모달 */}
        <NLComponentModal
          isOpen={showNLComponentModal}
          onClose={closeNLComponentModal}
          dashboardId={dashboardTemplate?.dashboard_id}
          existingComponent={componentToUpdate}
          onComponentCreated={(newComponent) => handleComponentCreated(newComponent, !!componentToUpdate)}
        />
        
        {/* 텍스트 라벨 전용 모달 제거 - 공용 모달로 통합 */}
        
        {/* 대시보드 충돌 모달 */}
        <DashboardConflictModal
          isOpen={showConflictModal}
          onClose={closeConflictModal}
          conflictInfo={conflictInfo}
          onReplaceExisting={handleReplaceExisting}
          onCreateNew={handleCreateNew}
          onDeleteOriginal={handleDeleteOriginal}
          onKeepBoth={handleKeepBoth}
          saving={conflictSaving}
        />
        
        {/* 배경 이미지 모달 */}
        <DashboardBackgroundModal
          isOpen={showBackgroundModal}
          onClose={closeBackgroundModal}
          dashboardId={dashboardTemplate?.dashboardId || dashboardTemplate?.dashboard_id}
          currentBackground={dashboardTemplate}
          onPendingBackgroundChange={(pending) => {
            setPendingBackground(pending);
          }}
        />

      {/* 플로팅 편집 버튼 - dashboard-container 바깥으로 이동 (관리자 전용, 팝업 모드에서는 숨김) */}
      {!editMode && isAdmin && !isPopup && (
        <div className="floating-edit-control-wrapper">
          <div className="floating-edit-control">
            <button
              className="floating-edit-btn"
              onClick={toggleEditMode}
              title="편집 모드 시작"
            >
              ✎
            </button>
          </div>
        </div>
      )}
      
      {/* 알람 관리 모달 */}
      <AlarmManagementModal 
        isOpen={showAlarmManagement}
        onClose={() => {
          setShowAlarmManagement(false);
          reloadAlarms();
        }}
      />
      
      {/* 알람 팝업 */}
      {activeAlarm && (
        <AlarmPopup
          alarmKey={activeAlarm.alarm_key}
          dashboardId={activeAlarm.dashboard_id}
          snoozeDuration={activeAlarm.snooze_duration}
          popupSizeMode={activeAlarm.popup_size_mode}
          popupWidth={activeAlarm.popup_width}
          popupHeight={activeAlarm.popup_height}
          popupPaddingVertical={activeAlarm.popup_padding_vertical}
          popupPaddingHorizontal={activeAlarm.popup_padding_horizontal}
          transparentMode={activeAlarm.popup_transparent_mode}
          onConfirm={handleAlarmConfirm}
          onSnooze={handleAlarmSnooze}
        />
      )}
      
      {/* Global Variables 디버그 패널 */}
      <GlobalVariablesDebugPanel />
      
      {/* 복사/붙여넣기 Toast 메시지 */}
      {showCopyToast && (
        <div className="toast-message toast-success">
          차트가 클립보드에 복사되었습니다
        </div>
      )}
      {showPasteToast && (
        <div className="toast-message toast-success">
          차트가 붙여넣기되었습니다
        </div>
      )}

    </div>
  );
});

export default Dashboard; 